import * as Sentry from "@sentry/browser";
import { Badge, Col, Collapse, Drawer, Empty, Input, message, Row, Spin, Button as AntdButton} from "antd";
import { FormInstance } from "antd/lib/form";
import { UploadFile } from "antd/lib/upload/interface";
import { Button } from "antd/lib/radio";
import { AxiosResponse } from "axios";
import debounce from "lodash.debounce";
import moment, { Moment } from "moment";
import React, { Fragment, RefObject } from "react";
import { RouteComponentProps } from "react-router";
import { Redirect } from "react-router-dom";
import Card from "../components/Card";
import EditButton from "../components/EditButton";
import EntitledSection from "../components/EntitledSection";
import BaseForm from "../components/Form/BaseForm";
import InteractionsSection from "../components/InteractionsSection";
import { tabPaneStyle } from "../helpers/constants";
import ModalCreator from "../components/ModalCreator";
import PreOpportunityInteractionsSection from "../components/PreOpportunityInteractionsSection";
import entitlements from "../config/entitlements";
import {
  ActiveDrawerContent,
  Changelog,
  Company,
  CompanyContact,
  Country,
  Customer,
  CustomerCompanyAssociation,
  Department,
  Director,
  FinancialInstitution,
  NotificationRecord,
  Opportunity,
  OpportunityInteraction,
  OpportunityProspect,
  PreOpportunityInteraction,
  Product,
  SalesPerson,
  Ticket,
  UpdateBeneficiaryInformationFnProps,
  UpdatePaymentInformationFnProps,
  UpdateCustomerCompanyAssociationFnProps,
  User,
  Beneficiary,
  PaymentAccount,
  ValidationStatus,
  MaritalStatus,
  Title,
  AssociatedEmail,
  AssociatedPhone,
  AssociatedAddress,
  APIError,
  CustomerBackgroundInformation,
  EmailInterface,
  PhoneInterface,
  AddressInterface,
  BranchMap,
  SelectOptionsInterface,
  FormFieldConfig,
  FileUpload,
} from "../helpers/shared-interfaces";
import {
  generateDisplayErrorMessage,
  getUniqueArrayObjects,
  handleValidationErrors,
  LOAD_OPPORTUNITY_LIGHT,
  mapAPIErrorToFormError,
  mergeCompanyPaymentInformation,
  mergeCustomerPaymentInformation,
  ProgressStatuses,
  request,
  responseHandler,
  spaceCamel,
  ticketSource,
  convertCustomersToSelectOptions,
  extractFileName,
  generateCompanySegments,
  filterProductsByProspect,
  generateContributionTypes,
  productPaymentTypes,
} from "../helpers/utilities";
import { Constants as AssociatedEntityConstants } from "../components/AssociatedEntitiesSection";
import {
  createAddressFields,
  createBackgroundInfoFields,
  createEmailFields,
  createPhoneFields,
} from "../components/AssociatedEntitiesSection";
import CheckList from "./BusinessDevelopment/CheckList";
import CompanyInformation from "./BusinessDevelopment/ViewOpportunity/CompanyInformation";
import IndividualInformation from "./BusinessDevelopment/ViewOpportunity/IndividualInformation";
import OpportunityDetails from "./BusinessDevelopment/ViewOpportunity/OpportunityDetails";
import EditOpportunity from "./EditOpportunity";
import { ChangelogTableFactory } from "./ViewCustomer";
import { getProgressStatus } from "./Opportunities";
import CustomerOnboardingDetails, {
  Views as CustomerOnboardingViews,
  createDocumentUploadFormFields as createCustomerDocumentUploadFormFields,
} from "../components/CustomerOnboardingDetails";
import CompanyOnboardingDetails, {
  createDocumentUploadFormFields,
  Views as CompanyOnboardingViews,
} from "../components/CompanyOnboardingDetails";
import { FileTypes } from "../helpers/shared-interfaces";
import { GET_COUNTRIES, GET_FINANCIAL_INSTITUTIONS } from "../gql/general";
import {
  ASSIGN_PRODUCT_TO_OPPORTUNITY,
  ASSIGN_USER_TO_OPPORTUNITY,
  CONTINUE_OPPORTUNITY,
  CREATE_OPPORTUNITY_INTERACTION,
  CREATE_OPPORTUNITY_TICKET,
  DELETE_OPPORTUNITY,
  GET_OPPORTUNITY,
  GET_OPPORTUNITY_CHANGELOG,
  GET_OPPORTUNITY_CHECKLIST,
  GET_OPPORTUNITY_FORM_DATA,
  GET_OPPORTUNITY_NOTIFIER_RECORDS_QUERY,
} from "../gql/opportunity";
import {
  CREATE_EMAIL,
  CREATE_PHONE,
  CREATE_ADDRESS,
  GET_INDUSTRY_SECTORS,
  GET_TRUSTEES_SUMMARY,
  GET_MARITAL_STATUSES,
  GET_TITLES,
  MARK_EMAIL_AS_PRIMARY,
  MARK_PHONE_AS_PRIMARY,
  MARK_ADDRESS_AS_PRIMARY,
} from "../gql/general";
import {
  ASSIGN_EMAIL_TO_COMPANY,
  ASSIGN_PHONE_TO_COMPANY,
  ASSIGN_ADDRESS_TO_COMPANY,
  UNASSIGN_EMAIL_FROM_COMPANY,
  UNASSIGN_PHONE_FROM_COMPANY,
  UNASSIGN_ADDRESS_FROM_COMPANY,
  MARK_COMPANY_EMAIL_AS_PRIMARY,
  MARK_COMPANY_PHONE_AS_PRIMARY,
  MARK_COMPANY_ADDRESS_AS_PRIMARY,
  UPDATE_COMPANY_EMAIL,
  UPDATE_COMPANY_PHONE,
  UPDATE_COMPANY_ADDRESS,
  UPDATE_COMPANY_MUTATION,
  CREATE_CONTACT_PERSON_MUTATION,
  UPDATE_COMPANY_CONTACT_PERSON_MUTATION,
  DELETE_COMPANY_CONTACT_PERSON_MUTATION,
  GET_COMPANIES,
} from "../gql/companies";
import {
  ASSIGN_EMAIL_TO_CUSTOMER,
  ASSIGN_PHONE_TO_CUSTOMER,
  ASSIGN_ADDRESS_TO_CUSTOMER,
  UNASSIGN_EMAIL_FROM_CUSTOMER,
  UNASSIGN_PHONE_FROM_CUSTOMER,
  UNASSIGN_ADDRESS_FROM_CUSTOMER,
  UPDATE_CUSTOMER_EMAIL,
  UPDATE_CUSTOMER_PHONE,
  UPDATE_CUSTOMER_ADDRESS,
  UPDATE_CUSTOMER_MUTATION,
  DELETE_CUSTOMER_ASSOCIATION,
  CREATE_CUSTOMER_BACKGROUND_INFORMATION,
  UPDATE_CUSTOMER_BACKGROUND_INFORMATION,
  SEARCH_CUSTOMER,
} from "../gql/customers";
import {
  DELETE_DEBIT_ACCOUNT,
  DELETE_PAYOUT_ACCOUNT,
} from "../gql/accounts";
import {
  DELETE_BENEFICIARY,
} from "../gql/beneficiaries";
import { GET_CONTACT_LEVELS } from "../gql/templates";
import { ALL_USERS_QUERY, GET_SALES_PEOPLE } from "../gql/users";
import { notificationColumns, NotificationRecordTableFactory } from "./NotificationRecords";
import { DEBOUNCE_TIME } from "../helpers/constants";
import { ghanaCardRegex } from "../helpers/constants";
import { GET_ROLE_SUMMARY } from "../gql/roles";
import { DELETE_FILE } from "../gql/general";
import {
  CREATE_COMPANY_DIRECTOR,
  CREATE_DIRECTOR_CONTACT,
  DELETE_DIRECTOR_CONTACT,
  REMOVE_DIRECTOR,
} from "../gql/companies";
import {
  remodelProducts,
  getContributionAmountFieldState,
  getContributionAllocationTypeOptions,
  normalizeContributionAmountPayload,
  sanitizeContributionAllocationTypes,
} from "./BusinessDevelopment/ProductForm";

const directorContactFieldsToValidate = ["emailAddress", "phoneNumber"];
const relationshipManagerRoleId = "29";
const STATUS_COLORS: Record<string, string> = {
  "Lead": "gold-btn",
  "PetraID Assigned": "success-btn",
  "In Progress": "blue-btn",
};
interface State {
  countries: Country[];
  opportunity: Opportunity | null;
  interactions: OpportunityInteraction[];
  preOpportunityInteractions?: PreOpportunityInteraction[];
  department: Department | null;
  isLoading: boolean;
  id: string | null;
  users: User[];
  canAccess: boolean;
  isEditingClientData: boolean;
  showChecklist: boolean;
  showDialog: boolean;
  isEditingProductData: boolean;
  isTicketCreating: boolean;
  checklistData: {
    percentage: number;
    result: ValidationStatus[];
  };
  opportunityChangeLogs: Changelog[];
  showDrawer: boolean;
  loadingChangelog: boolean;
  isFiltering: boolean;
  filterBy: string;
  filteredOpportunityChangeLogs: Changelog[];
  isSearching: boolean;
  userSearchTerm: string;
  redirect: boolean;
  activeDrawerContent: ActiveDrawerContent;
  showCancelModal: boolean;
  notifications?: NotificationRecord[];
  showContactModal: boolean;
  showContactDeleteModal: boolean;
  contactModalType: "email" | "phone" | "address" | null;
  contactModalMode: "add" | "edit";
  contactModalLoading: boolean;
  deleteContactType: "email" | "phone" | "address" | null;
  isExistingCustomer: boolean;
  companyContactFieldsToHide: string[];
  contactPersonRequired: boolean;
  customerSearchTerm: string;
  customers: Customer[];
  deleteContactId: string | null;
  selectedEmail?: AssociatedEmail | null;
  selectedPhone?: AssociatedPhone | null;
  selectedAddress?: AssociatedAddress | null;
  addressType: string;
  companies: Company[];
  contactLevels: SelectOptionsInterface[];
  financialInstitutions: FinancialInstitution[];
  branchMap: BranchMap;
  showBeneficiariesModal: boolean;
  showPaymentAccountsModal: boolean;
  beneficiariesActiveKey?: string;
  paymentAccountsActiveKey?: string;
  showAssociationsModal: boolean;
  associationsActiveKey: string;
  showStandaloneDeleteModal: boolean;
  deleteTargetType: "association" | "companyContact" | "beneficiary" | "payout" | "debit" | "director" | null;
  deleteTargetId: string | null;
  showDeleteConfirmationModal: boolean;
  modalTarget: string;
  showCompanyContactModal: boolean;
  showCompanyContactDeleteModal: boolean;
  currentCompanyContact?: CompanyContact | null;
  showCompanyDirectorModal: boolean;
  showCompanyDirectorContactModal: boolean;
  currentCompanyDirector?: Director | null;
  directorToDelete?: Director | null;
  showAddDocumentModal: boolean;
  idToBeDeleted?: string | null;
  showCompanyPaymentModal: boolean;
  companyPaymentActiveKey?: string;
  showBackgroundInfoModal: boolean;
  showBackgroundInfoDeleteModal: boolean;
  backgroundInfoMode: "add" | "edit";
  isBackgroundInfoSaving: boolean;
  fatherDeceased: boolean;
  motherDeceased: boolean;
  documentUrl: string;
  titles: Title[];
  maritalStatuses: MaritalStatus[];
  industrySectors: SelectOptionsInterface[];
  trustees: SelectOptionsInterface[];
  relationshipManagers: SelectOptionsInterface[];
  isClientEditVerified: boolean;
  photoIdUrl: string;
  ghanaCardIdUrl: string;
  ghanaCardIdBackUrl: string;
  signatureUrl: string;
  verificationProofUrl: string;
  isClientEditSaving: boolean;
  showProductEditModal: boolean;
  isProductEditSaving: boolean;
  productCategoryAndTier: string;
  salesPersons: SalesPerson[];
  salesPersonSearchTerm: string;
  petraProducts: Product[];
  productEditFieldsToHide: string[];
}
interface RouteParams {
  id: string;
}
interface Props extends RouteComponentProps<RouteParams> {
  user: User;
}
export default class ViewOpportunity extends React.Component<Props, State> {
  reference: RefObject<FormInstance>;
  contactEmailReference: RefObject<FormInstance>;
  contactPhoneReference: RefObject<FormInstance>;
  contactAddressReference: RefObject<FormInstance>;
  beneficiariesModalRef: RefObject<CustomerOnboardingDetails>;
  paymentAccountsModalRef: RefObject<CustomerOnboardingDetails>;
  associationsModalRef: RefObject<CustomerOnboardingDetails>;
  companyContactPersonFormReference: RefObject<FormInstance>;
  backgroundInfoReference: RefObject<FormInstance>;
  newCompanyDirectorFormReference: RefObject<FormInstance>;
  newCompanyDirectorContactFormReference: RefObject<FormInstance>;
  docsReference: RefObject<FormInstance>;
  companyPaymentModalRef: RefObject<CompanyOnboardingDetails>;
  clientEditReference: RefObject<FormInstance>;
  productEditReference: RefObject<FormInstance>;

  constructor(props: Readonly<Props>) {
    super(props);
    this.state = {
      id: null,
      users: [],
      countries: [],
      opportunity: null,
      isLoading: true,
      department: null,
      interactions: [],
      preOpportunityInteractions: [],
      canAccess: true,
      isEditingClientData: false,
      showDialog: false,
      showChecklist: false,
      isEditingProductData: false,
      isTicketCreating: false,
      checklistData: {
        percentage: 0,
        result: [],
      },
      opportunityChangeLogs: [],
      showDrawer: false,
      loadingChangelog: false,
      filterBy: "",
      isFiltering: false,
      filteredOpportunityChangeLogs: [],
      isSearching: false,
      userSearchTerm: "",
      redirect: false,
      activeDrawerContent: "",
      showCancelModal: false,
      showContactModal: false,
      showContactDeleteModal: false,
      contactModalType: null,
      contactModalMode: "add",
      contactModalLoading: false,
      deleteContactType: null,
      deleteContactId: null,
      companyContactFieldsToHide: ["customerId"],
      contactPersonRequired: false,
      customerSearchTerm: "",
      customers: [],
      selectedEmail: null,
      selectedPhone: null,
      selectedAddress: null,
      addressType: AssociatedEntityConstants.PhysicalAddressCode,
      companies: [],
      contactLevels: [],
      financialInstitutions: [],
      showBeneficiariesModal: false,
      showPaymentAccountsModal: false,
      beneficiariesActiveKey: undefined,
      paymentAccountsActiveKey: undefined,
      showAssociationsModal: false,
      associationsActiveKey: "",
      showStandaloneDeleteModal: false,
      deleteTargetType: null,
      deleteTargetId: null,
      showDeleteConfirmationModal: false,
      modalTarget: "",
      showCompanyContactModal: false,
      showCompanyContactDeleteModal: false,
      currentCompanyContact: null,
      isExistingCustomer: false,
      branchMap: {},
      showCompanyDirectorModal: false,
      showCompanyDirectorContactModal: false,
      currentCompanyDirector: null,
      directorToDelete: undefined,
      showAddDocumentModal: false,
      idToBeDeleted: undefined,
      showCompanyPaymentModal: false,
      companyPaymentActiveKey: undefined,
      showBackgroundInfoModal: false,
      showBackgroundInfoDeleteModal: false,
      backgroundInfoMode: "add",
      isBackgroundInfoSaving: false,
      fatherDeceased: false,
      motherDeceased: false,
      documentUrl: "",
      titles: [],
      maritalStatuses: [],
      industrySectors: [],
      trustees: [],
      relationshipManagers: [],
      isClientEditVerified: false,
      photoIdUrl: "",
      ghanaCardIdUrl: "",
      ghanaCardIdBackUrl: "",
      signatureUrl: "",
      verificationProofUrl: "",
      isClientEditSaving: false,
      showProductEditModal: false,
      isProductEditSaving: false,
      productCategoryAndTier: "",
      salesPersons: [],
      salesPersonSearchTerm: "",
      petraProducts: [],
      productEditFieldsToHide: ["monthlyExpectedContribution", "lumpsumExpectedContribution"],
    };

    this.reference = React.createRef<FormInstance>();
    this.contactEmailReference = React.createRef<FormInstance>();
    this.contactPhoneReference = React.createRef<FormInstance>();
    this.contactAddressReference = React.createRef<FormInstance>();
    this.beneficiariesModalRef = React.createRef<CustomerOnboardingDetails>();
    this.paymentAccountsModalRef = React.createRef<CustomerOnboardingDetails>();
    this.associationsModalRef = React.createRef<CustomerOnboardingDetails>();
    this.companyContactPersonFormReference = React.createRef<FormInstance>();
    this.backgroundInfoReference = React.createRef<FormInstance>();
    this.newCompanyDirectorFormReference = React.createRef<FormInstance>();
    this.newCompanyDirectorContactFormReference =
      React.createRef<FormInstance>();
    this.docsReference = React.createRef<FormInstance>();
    this.companyPaymentModalRef = React.createRef<CompanyOnboardingDetails>();
    this.clientEditReference = React.createRef<FormInstance>();
    this.productEditReference = React.createRef<FormInstance>();
  }

  setRef = (ref: React.RefObject<FormInstance>): void => {
    this.reference = ref;
  };

  reassignDefaultView(): JSX.Element {
    const { opportunity } = this.state;
    return (
      <Fragment>
        {opportunity && (
          <Col span={6}>
            {opportunity.id && (
              <div
                style={{
                  position: "relative",
                  top: "10px",
                  left: "22px",
                  textAlign: "left",
                }}
              >
                <span
                  style={{
                    fontSize: "14px",
                    color: "rgba(0, 0, 0, 0.85)",
                  }}
                >
                  <b>Assigned To</b>
                </span>
                {": "}
                {opportunity.assignee && opportunity.assignee.name}
              </div>
            )}
          </Col>
        )}
      </Fragment>
    );
  }

  generateOpportunityStatus = (name: string): string => {
    if (
      name === ProgressStatuses.inProgress ||
      name === ProgressStatuses.onboardingInitated ||
      name === ProgressStatuses.pendingMatch
    ) {
      return "In Progress";
    } else if (name === ProgressStatuses.incomplete) {
      return "Incomplete";
    } else if (name === ProgressStatuses.lead) {
      return "Lead";
    } else if (name === ProgressStatuses.won) {
      return "Won";
    } else {
      return "Cancelled";
    }
  };

  getAllUsers(): void {
    const { userSearchTerm } = this.state;
    request({
      query: ALL_USERS_QUERY,
      variables: {
        page: 1,
        q: userSearchTerm,
      },
    }).then(({ data: { data } }: any): void => {
      this.setState({
        users: data.users.results,
        isLoading: false,
      });
    });
  }

  triggerUserDelayedSearch = debounce(async (): Promise<void> => {
    this.getAllUsers();
  }, DEBOUNCE_TIME);

  handleUserSearchInput = async (userSearchTerm: string): Promise<void> => {
    this.setState({ userSearchTerm }, () => {
      this.triggerUserDelayedSearch();
    });
  };

  createInteraction = async (): Promise<void> => {
    const { id } = this.state;
    const { user } = this.props;

    interface Validated {
      description: string;
      interactionModeId: string;
      interactionDate: Moment;
    }

    interface Response {
      interactions: OpportunityInteraction[];
    }

    const fieldsToBeValidated: string[] = [
      "description",
      "interactionModeId",
      "interactionDate",
    ];

    if (!this.reference.current) return;

    let data;

    try {
      data = await this.reference.current.validateFields(fieldsToBeValidated);
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    this.setState({ isLoading: true });

    try {
      const response: AxiosResponse = await request({
        query: CREATE_OPPORTUNITY_INTERACTION,
        variables: {
          interactionDate:
            data.interactionDate && data.interactionDate.format("YYYY-MM-DD"),
          interactionModeId: data.interactionModeId,
          description: data.description,
          opportunityId: id,
          userId: user.id,
        },
      });
      this.setState({ isLoading: false });

      const { err, mutation } = responseHandler<Response>(
        response,
        "createOpportunityInteraction",
      );
      if (err) {
        message.error(`An Error Occurred - ${generateDisplayErrorMessage(err)}`);
        return;
      } else {
        this.setState(
          {
            interactions: mutation.interactions,
          },
          () => {
            this.createTicket();
          },
        );
        message.success("Interaction created successfully");
        this.reference.current &&
          this.reference.current.resetFields(fieldsToBeValidated);
        this.getOpportunity();
      }
    } catch (error) {
      Sentry.captureException(error);

      message.error(`Failed to create Interaction - ${generateDisplayErrorMessage(error)}`);
      this.setState({ isLoading: false });
    }
  };

  getCountries(): void {
    interface Response {
      data: {
        countries: Country[];
      };
    }
    request({
      query: GET_COUNTRIES,
    }).then(({ data }: AxiosResponse<Response>): void => {
      this.setState({ countries: data.data.countries });
    });
  }

  getCompanies = async (): Promise<void> => {
    interface Response {
      data: {
        companies: Company[];
      };
    }
    try {
      const {
        data: { data },
      }: AxiosResponse<Response> = await request({
        query: GET_COMPANIES,
      });
      this.setState({ companies: data.companies || [] });
    } catch (error) {
      message.error("Failed to load companies");
      Sentry.captureException(error);
    }
  };

  getFinancialInstitutions = async (): Promise<void> => {
    interface Response {
      data: {
        financialInstitutions: FinancialInstitution[];
      };
    }

    try {
      const {
        data: { data },
      }: AxiosResponse<Response> = await request({
        query: GET_FINANCIAL_INSTITUTIONS,
      });

      const financialInstitutions = data.financialInstitutions || [];
      const branchMap: BranchMap = {};
      financialInstitutions.forEach((institution) => {
        branchMap[institution.id] = institution.bankBranches;
      });

      this.setState({ financialInstitutions, branchMap });
    } catch (error) {
      message.error("Failed to load financial institutions");
      Sentry.captureException(error);
    }
  };

  openBeneficiariesModal = (
    beneficiaryOrEvent?: Beneficiary | React.MouseEvent,
  ): void => {
    const beneficiary =
      beneficiaryOrEvent && "id" in beneficiaryOrEvent
        ? (beneficiaryOrEvent as Beneficiary)
        : undefined;

    this.setState(
      {
        showBeneficiariesModal: true,
        beneficiariesActiveKey: CustomerOnboardingViews.beneficiary,
      },
      () => {
        window.setTimeout(() => {
          if (beneficiary) {
            this.beneficiariesModalRef.current?.setCurrentlyEditing(
              CustomerOnboardingViews.beneficiary,
              beneficiary,
            );
          } else {
            this.beneficiariesModalRef.current?.resetState();
          }
        }, 0);
      },
    );
  };

  openBeneficiariesDeleteModal = (beneficiary: Beneficiary): void => {
    this.setState({
      showStandaloneDeleteModal: true,
      deleteTargetType: "beneficiary",
      deleteTargetId: beneficiary.id,
    });
  };

  closeBeneficiariesModal = (): void => {
    this.beneficiariesModalRef.current?.resetState();
    this.setState({ showBeneficiariesModal: false });
  };

  openPaymentAccountsModal = (
    account?: PaymentAccount,
    type?: "payout" | "debit",
  ): void => {
    if (!(this.state.financialInstitutions?.length ?? 0)) {
      void this.getFinancialInstitutions();
    }

    this.setState(
      {
        showPaymentAccountsModal: true,
        paymentAccountsActiveKey:
          type === "debit"
            ? CustomerOnboardingViews.debit
            : CustomerOnboardingViews.payout,
      },
      () => {
        window.setTimeout(() => {
          const hasCustomer = !!this.state.opportunity?.customer;
          const hasCompany = !!this.state.opportunity?.company;
          const companyPaymentMode = type === "debit" ? "debit" : "payout";

          if (account && type) {
            if (hasCustomer) {
              this.paymentAccountsModalRef.current?.setCurrentlyEditing(
                type === "payout"
                  ? CustomerOnboardingViews.payout
                  : CustomerOnboardingViews.debit,
                account,
              );
            } else if (hasCompany) {
              this.companyPaymentModalRef.current?.setCurrentlyEditing(
                companyPaymentMode,
                account,
              );
            }
          } else {
            if (hasCustomer) {
              this.paymentAccountsModalRef.current?.resetState();
            } else if (hasCompany) {
              this.companyPaymentModalRef.current?.resetState();
            }
          }
        }, 0);
      },
    );
  };

  openPaymentAccountsDeleteModal = (
    account: PaymentAccount,
    type: "payout" | "debit",
  ): void => {
    this.setState({
      showStandaloneDeleteModal: true,
      deleteTargetType: type,
      deleteTargetId: account.id,
    });
  };

  closePaymentAccountsModal = (): void => {
    this.paymentAccountsModalRef.current?.resetState();
    this.companyPaymentModalRef.current?.resetState();
    this.setState({ showPaymentAccountsModal: false });
  };

  loadContactLevels = async (): Promise<void> => {
    interface Response {
      data: {
        contactLevels: SelectOptionsInterface[];
      };
    }

    try {
      const {
        data: { data },
      }: AxiosResponse<Response> = await request({
        query: GET_CONTACT_LEVELS,
      });

      this.setState({ contactLevels: data.contactLevels || [] });
    } catch (error) {
      message.error("Failed to load contact levels");
      Sentry.captureException(error);
    }
  };

  openAssociationsModal = (association?: CustomerCompanyAssociation): void => {
    if (!this.state.companies.length) {
      this.getCompanies();
    }
    this.setState(
      {
        showAssociationsModal: true,
        associationsActiveKey: CustomerOnboardingViews.customerAssociation,
      },
      () => {
        if (association) {
          this.associationsModalRef.current?.setCurrentlyEditing(
            CustomerOnboardingViews.customerAssociation,
            association,
          );
        } else {
          this.associationsModalRef.current?.resetState();
        }
      },
    );
  };

  closeAssociationsModal = (): void => {
    this.associationsModalRef.current?.resetState();
    this.setState({ showAssociationsModal: false });
  };

  setAssociationEmploymentStatus = async (
    association: CustomerCompanyAssociation,
    isEmployee: boolean,
  ): Promise<void> => {
    const associationsView = this.associationsModalRef.current;
    if (!associationsView) return;

    await (isEmployee
      ? associationsView.markAsCurrent(association.id)
      : associationsView.markAsFormer(association.id));

    // markAsFormer/markAsCurrent only update local state, so refetch to move
    // the association between the current and former panels.
    this.getOpportunity();
  };

  openAssociationsDeleteModal = (
    association: CustomerCompanyAssociation,
  ): void => {
    this.setState({
      showStandaloneDeleteModal: true,
      deleteTargetType: "association",
      deleteTargetId: association.id,
    });
  };

  closeStandaloneDeleteModal = (): void => {
    this.setState({
      showStandaloneDeleteModal: false,
      deleteTargetType: null,
      deleteTargetId: null,
    });
  };

  updateCustomerCompanyAssociationsInformation = (
    props: UpdateCustomerCompanyAssociationFnProps,
  ): void => {
    const { opportunity } = this.state;
    if (!opportunity || !opportunity.customer) return;

    const updatedOpportunity: Opportunity = {
      ...opportunity,
      customer: {
        ...opportunity.customer,
        customerCompanyAssociations: props.associations,
      },
    };

    this.setState({ opportunity: updatedOpportunity });
  };

  openCompanyContactModal = (contact?: CompanyContact): void => {
    if (!this.state.contactLevels.length) {
      this.loadContactLevels();
    }

    this.setState(
      {
        showCompanyContactModal: true,
        currentCompanyContact: contact || null,
        customerSearchTerm: contact?.customer?.petraId ?? "",
        isExistingCustomer: !!contact?.customer,
      },
      () => {
        this.handleIsExistingCustomerchecked(!!contact?.customer);
        if (contact) {
          this.companyContactPersonFormReference.current?.setFieldsValue({
            contactName: contact.contactName,
            emailAddress: contact.emailAddress,
            mobileNumber: contact.mobileNumber,
            role: contact.role,
            contactLevelIds: contact.contactLevels?.map((level) => level.id),
            isExistingCustomer: !!contact.customer,
            documentUrl: contact.documentUrl || "",
            customerId: contact.customer?.id,
          });
        } else {
          this.companyContactPersonFormReference.current?.resetFields();
          this.setState({ isExistingCustomer: false });
        }
      },
    );
  };

  closeCompanyContactModal = (): void => {
    this.setState({
      showCompanyContactModal: false,
      currentCompanyContact: null,
      customerSearchTerm: "",
      isExistingCustomer: false,
      companyContactFieldsToHide: ["customerId"],
      contactPersonRequired: false,
      documentUrl: "",
    });
    this.companyContactPersonFormReference.current?.resetFields();
  };

  handleIsExistingCustomerchecked = (value: boolean): void => {
    if (!value) {
      this.setState({
        isExistingCustomer: value,
        companyContactFieldsToHide: [
          ...this.state.companyContactFieldsToHide,
          "customerId",
        ],
      });
    } else {
      this.setState({
        isExistingCustomer: value,
        companyContactFieldsToHide:
          this.state.companyContactFieldsToHide.filter(
            (item) => item !== "customerId",
          ),
      }, () => {
        if (!this.state.customers.length) {
          void this.triggerCustomerSearch();
        }
      });
    }
  };

  handleFileUploadChange = (
    field: string,
    combinedInfo: string,
  ): void => {
    const [url] = combinedInfo.split("@@@");
    this.companyContactPersonFormReference.current?.setFieldsValue({
      [field]: combinedInfo,
    });
    if (field === "documentUrl") {
      this.setState({ documentUrl: url });
    }
  };

  handleContactLevelsChange = (
    value: string | string[],
    options: any | any[],
  ): void => {
    if (window.location.pathname.split("/").includes("business-development")) {
      this.setState({
        contactPersonRequired: false,
      });
      return;
    }

    const optionsArray = Array.isArray(options) ? options : [options];
    const contactPersonRequired = !optionsArray.some(
      (option) => option.children === "RM" || option.children === "Client Services",
    );

    this.setState({
      contactPersonRequired,
    });
  };

  triggerCustomerSearch = async (): Promise<void> => {
    try {
      const { customerSearchTerm: q } = this.state;
      const searchQuery = q && q.trim().length > 0 ? q : " ";

      const r: AxiosResponse = await request({
        query: SEARCH_CUSTOMER,
        variables: { q: searchQuery, page: 1 },
      });

      if (r.data?.errors) {
        message.error("customer search failed");
      } else {
        this.setState({
          customers: r.data?.data?.customerSearch?.results || [],
        });
      }
    } catch (error) {
      message.error("customer search failed");
      Sentry.captureException(error);
    }
  };

  triggerCustomerDelayedSearch = debounce(async (): Promise<void> => {
    await this.triggerCustomerSearch();
  }, DEBOUNCE_TIME);

  handleCustomerSearchInput = async (
    customerSearchTerm: string,
  ): Promise<void> => {
    this.setState({ customerSearchTerm });
    await this.triggerCustomerDelayedSearch();
  };

  saveCompanyContactPerson = async (): Promise<void> => {
    const { opportunity, currentCompanyContact } = this.state;
    if (!opportunity?.company || !this.companyContactPersonFormReference.current) {
      return;
    }

    let data;
    try {
      data = await this.companyContactPersonFormReference.current.validateFields([
        "contactName",
        "emailAddress",
        "mobileNumber",
        "contactLevelIds",
        "role",
        "documentUrl",
        "customerId",
      ]);
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    this.setState({ contactModalLoading: true });

    try {
      const query = currentCompanyContact
        ? UPDATE_COMPANY_CONTACT_PERSON_MUTATION
        : CREATE_CONTACT_PERSON_MUTATION;

      const response = await request({
        query,
        variables: currentCompanyContact
          ? {
              id: currentCompanyContact.id,
              companyId: opportunity.company.id,
              contactLevelIds: data.contactLevelIds,
              contactName: data.contactName,
              emailAddress: data.emailAddress,
              mobileNumber: data.mobileNumber,
              customerId: data.customerId,
              role: data.role,
              documentUrl:
                this.state.documentUrl || currentCompanyContact.documentUrl,
            }
          : {
              companyId: opportunity.company.id,
              contactLevelIds: data.contactLevelIds,
              contactName: data.contactName,
              emailAddress: data.emailAddress,
              mobileNumber: data.mobileNumber,
              customerId: data.customerId,
              isExistingCustomer: this.state.isExistingCustomer,
              role: data.role,
              documentUrl: this.state.documentUrl,
            },
      });

      const mutationName = currentCompanyContact
        ? "updateCompanyContact"
        : "createCompanyContact";
      const { err, mutation } = responseHandler<{
        companyContact?: CompanyContact;
      }>(response, mutationName);

      if (err) {
        this.companyContactPersonFormReference.current.setFields(
          err.map(mapAPIErrorToFormError),
        );
        message.error(generateDisplayErrorMessage(err));
        return;
      }

      if (!currentCompanyContact && !mutation.companyContact) {
        message.error("Company contact creation failed");
        return;
      }

      message.success(
        currentCompanyContact
          ? "Contact person updated successfully"
          : "Contact person created successfully",
      );
      this.closeCompanyContactModal();
      this.getOpportunity();
    } catch (error) {
      message.error("Failed to save contact person");
      Sentry.captureException(error);
    } finally {
      this.setState({ contactModalLoading: false });
    }
  };

  openCompanyContactDeleteModal = (contact: CompanyContact): void => {
    this.setState({
      showStandaloneDeleteModal: true,
      deleteTargetType: "companyContact",
      deleteTargetId: contact.id,
    });
  };

  confirmStandaloneDelete = async (): Promise<void> => {
    const { deleteTargetType, deleteTargetId } = this.state;
    if (!deleteTargetType || !deleteTargetId) return;

    this.setState({ contactModalLoading: true });

    try {
      let query = "";
      let mutationName = "";

      if (deleteTargetType === "association") {
        query = DELETE_CUSTOMER_ASSOCIATION;
        mutationName = "deleteCustomerCompanyAssociation";
      } else if (deleteTargetType === "companyContact") {
        query = DELETE_COMPANY_CONTACT_PERSON_MUTATION;
        mutationName = "deleteCompanyContact";
      } else if (deleteTargetType === "beneficiary") {
        query = DELETE_BENEFICIARY;
        mutationName = "deleteCustomerBeneficiary";
      } else if (deleteTargetType === "payout") {
        query = DELETE_PAYOUT_ACCOUNT;
        mutationName = "deleteCustomerPayoutAccount";
      } else if (deleteTargetType === "debit") {
        query = DELETE_DEBIT_ACCOUNT;
        mutationName = "deleteCustomerDebitAccount";
      } else if (deleteTargetType === "director") {
        await this.deleteCompanyDirector();
        return;
      }

      const response = await request({
        query,
        variables: { id: deleteTargetId },
      });

      const { err } = responseHandler(response, mutationName);
      if (!err) {
        message.success("Deleted successfully");
        this.closeStandaloneDeleteModal();
        this.getOpportunity();
      } else {
        message.error("Failed to delete");
      }
    } catch (error) {
      message.error("Failed to delete");
      Sentry.captureException(error);
    } finally {
      this.setState({ contactModalLoading: false });
    }
  };

  toggleCompanyDirectorModal = (open?: boolean): void => {
    this.setState((prevState) => ({
      showCompanyDirectorModal:
        open !== undefined ? open : !prevState.showCompanyDirectorModal,
    }));
  };

  openCompanyDirectorContactModal = (director: Director): void => {
    this.setState(
      {
        showCompanyDirectorContactModal: true,
        currentCompanyDirector: director,
      },
      () => {
        this.newCompanyDirectorContactFormReference?.current?.resetFields();
      },
    );
  };

  closeCompanyDirectorContactModal = (): void => {
    this.setState({
      showCompanyDirectorContactModal: false,
      currentCompanyDirector: null,
    });
    this.newCompanyDirectorContactFormReference?.current?.resetFields();
  };

  openCompanyDirectorDeleteModal = (director: Director): void => {
    this.setState({
      showStandaloneDeleteModal: true,
      deleteTargetType: "director",
      deleteTargetId: director.id,
    });
  };

  closeCompanyDirectorDeleteModal = (): void => {
    this.setState({
      showStandaloneDeleteModal: false,
      deleteTargetType: null,
      deleteTargetId: null,
    });
  };

  deleteCompanyDirector = async (): Promise<void> => {
    const { opportunity, deleteTargetId } = this.state;
    if (!opportunity?.company || !deleteTargetId) {
      return;
    }

    this.setState({ isLoading: true });

    try {
      const response = await request({
        query: REMOVE_DIRECTOR,
        variables: {
          companyId: opportunity.company.id,
          directorId: deleteTargetId,
        },
      });

      const { data: responseData } = response as AxiosResponse<any>;
      const errors =
        responseData?.data?.removeCompanyDirector?.errors as APIError[];

      if (errors && errors.length > 0) {
        message.error(`${errors[0].property}: ${errors[0].description}`);
      } else {
        message.success("Director removed successfully");
        await this.getOpportunity();
      }
    } catch (error) {
      message.error("Failed to remove director");
      console.error(error);
    } finally {
      this.setState({ isLoading: false });
      this.closeCompanyDirectorDeleteModal();
    }
  };

  deleteDirectorContact = async (
    contact: string,
    directorId: string,
  ): Promise<void> => {
    interface Response {
      director: Director;
      errors: APIError[];
    }

    this.setState({ isLoading: true });

    const isEmail = contact.includes("@");

    try {
      const response: AxiosResponse<Response> = await request({
        query: DELETE_DIRECTOR_CONTACT,
        variables: {
          directorId,
          emailAddress: isEmail ? contact : undefined,
          phoneNumber: isEmail ? undefined : contact,
        },
      });

      const { mutation } = responseHandler<Response>(
        response,
        "removeDirectorContact",
      );
      if (mutation?.director) {
        message.success("Contact removed successfully");
        await this.getOpportunity();
      } else {
        message.error("Failed to delete director contact");
      }
    } catch (error) {
      message.error("Failed to delete director contact");
      Sentry.captureException(error);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  createCompanyDirectorContact = async (): Promise<void> => {
    interface Response {
      data: {
        addDirectorContact: {
          director: Director;
          errors: APIError[];
        };
      };
    }

    const { currentCompanyDirector } = this.state;
    if (!currentCompanyDirector) return;

    let data;

    try {
      data =
        await this.newCompanyDirectorContactFormReference?.current?.validateFields(
          directorContactFieldsToValidate,
        );
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    this.setState({ isLoading: true });

    request({
      query: CREATE_DIRECTOR_CONTACT,
      variables: {
        ...data,
        directorId: currentCompanyDirector.id,
      },
    })
      .then(({ data: { data } }: AxiosResponse<Response>): void => {
        if (data?.addDirectorContact?.director) {
          const { errors } = data.addDirectorContact;
          if (errors && errors.length > 0) {
            message.error(`${errors[0].property}: ${errors[0].description}`);
          } else {
            message.success("Contact added successfully!");
          }
          this.getOpportunity();
          this.closeCompanyDirectorContactModal();
        } else {
          message.error("Request Failed");
        }
      })
      .catch((error) => {
        message.error("Error Performing Request");
        Sentry.captureException(JSON.stringify(error, null, 4));
      })
      .finally(() => this.setState({ isLoading: false }));
  };

  createCompanyDirector = async (): Promise<void> => {
    const company = this.state.opportunity?.company;
    if (!company?.id) {
      message.error(
        "No company is linked to this opportunity yet; cannot add a director.",
      );
      return;
    }

    let data;
    try {
      if (!this.newCompanyDirectorFormReference.current) {
        return;
      }
      data = await this.newCompanyDirectorFormReference.current.validateFields([
        "firstName",
        "lastName",
      ]);
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    this.setState({ isLoading: true });

    try {
      const response = await request({
        query: CREATE_COMPANY_DIRECTOR,
        variables: {
          ...data,
          companyId: company.id,
        },
      });

      const { data: responseData } = response as AxiosResponse<any>;
      const errors =
        responseData?.data?.createCompanyDirector?.errors as APIError[];

      if (errors && errors.length > 0) {
        message.error(`${errors[0].property}: ${errors[0].description}`);
      } else {
        message.success("Director added successfully!");
        await this.getOpportunity();
        this.newCompanyDirectorFormReference?.current?.resetFields();
        this.toggleCompanyDirectorModal(false);
      }
    } catch (error) {
      message.error("Error performing request");
      console.error(error);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  toggleFileModal = (open?: boolean): void => {
    this.setState((prevState) => ({
      showAddDocumentModal:
        open !== undefined ? open : !prevState.showAddDocumentModal,
    }));
  };

  handleDocumentUploadSuccess = (): void => {
    this.getOpportunity();
    this.loadChecklist();
  };

  deleteFile = async (id: string): Promise<void> => {
    if (!id) return;

    this.setState({ isLoading: true });
    try {
      const response = await request({
        query: DELETE_FILE,
        variables: { id },
      });
      const { mutation } = responseHandler<{ result: boolean; errors: APIError[] }>(
        response,
        "deleteFileUpload",
      );
      if (mutation.result) {
        message.success("File deleted successfully");
        await this.getOpportunity();
        this.loadChecklist();
      } else {
        message.error("Failed to delete file");
      }
    } catch (error) {
      message.error("Failed to delete a file");
      console.error(error);
    } finally {
      this.setState({ isLoading: false });
      this.toggleDeleteConfirmationModal();
    }
  };

  deleteSignedApplicationForm = async (): Promise<void> => {
    const customerUpload =
      this.state.opportunity?.customer?.customerInfoFileUploads?.find(
        (file) => file.fileType === FileTypes.signedApplicationForm,
      );
    if (customerUpload?.id) {
      await this.deleteFile(customerUpload.id);
      return;
    }

    const opportunity =
      this.state.opportunity || this.state.opportunity?.company?.opportunities?.[0];
    if (opportunity?.signedApplicationForm?.id) {
      await this.deleteFile(opportunity.signedApplicationForm.id);
    }
  };

  deleteVestingRuleDocument = async (): Promise<void> => {
    const opportunity =
      this.state.opportunity || this.state.opportunity?.company?.opportunities?.[0];
    if (opportunity?.vestingRuleDocument?.id) {
      await this.deleteFile(opportunity.vestingRuleDocument.id);
    }
  };

  deleteEmployeeDatasheet = async (): Promise<void> => {
    const opportunity =
      this.state.opportunity || this.state.opportunity?.company?.opportunities?.[0];
    if (opportunity?.employeeDatasheet?.id) {
      await this.deleteFile(opportunity.employeeDatasheet.id);
    }
  };

  deleteNPRAForm = async (): Promise<void> => {
    const opportunity =
      this.state.opportunity || this.state.opportunity?.company?.opportunities?.[0];
    if (opportunity?.npraForm?.id) {
      await this.deleteFile(opportunity.npraForm.id);
    }
  };

  deleteDirectDebitAuthorizationForm = async (): Promise<void> => {
    const customerUpload =
      this.state.opportunity?.customer?.customerInfoFileUploads?.find(
        (file) => file.fileType === FileTypes.directDebitAuthorizationForm,
      );
    if (customerUpload?.id) {
      await this.deleteFile(customerUpload.id);
      return;
    }

    const opportunity = this.state.opportunity;
    if (opportunity?.directDebitAuthorizationForm?.id) {
      await this.deleteFile(opportunity.directDebitAuthorizationForm.id);
    }
  };

  deleteCompanyDocument = async (): Promise<void> => {
    const { idToBeDeleted } = this.state;
    if (!idToBeDeleted) return;

    this.setState({ isLoading: true });
    try {
      const response = await request({
        query: DELETE_FILE,
        variables: { id: idToBeDeleted },
      });
      const { mutation } = responseHandler<{ result: boolean; errors: APIError[] }>(response, "deleteFileUpload");
      if (mutation.result) {
        message.success("File deleted successfully");
        await this.getOpportunity();
        this.loadChecklist();
      } else {
        message.error("Failed to delete file");
      }
    } catch (error) {
      message.error("Failed to delete a file");
      console.error(error);
    } finally {
      this.setState({ isLoading: false });
      this.toggleDeleteConfirmationModal();
    }
  };

  deleteCustomerDocument = async (): Promise<void> => {
    const { idToBeDeleted } = this.state;
    if (!idToBeDeleted) return;
    await this.deleteFile(idToBeDeleted);
  };

  confirmDeleteDocument = async (): Promise<void> => {
    const { modalTarget } = this.state;
    const normalizedTarget = (modalTarget || "")
      .toLowerCase()
      .replace(/[\s_-]/g, "");

    if (
      modalTarget === FileTypes.signedApplicationForm ||
      normalizedTarget.includes("signedapplication")
    ) {
      await this.deleteSignedApplicationForm();
      return;
    }
    if (
      modalTarget === FileTypes.directDebitAuthorizationForm ||
      normalizedTarget.includes("directdebit")
    ) {
      await this.deleteDirectDebitAuthorizationForm();
      return;
    }
    if (
      modalTarget === FileTypes.vestingRuleDocument ||
      normalizedTarget.includes("vesting")
    ) {
      await this.deleteVestingRuleDocument();
      return;
    }
    if (
      modalTarget === FileTypes.employeeDatasheet ||
      normalizedTarget.includes("employee") ||
      normalizedTarget === "eds"
    ) {
      await this.deleteEmployeeDatasheet();
      return;
    }
    if (modalTarget === FileTypes.npraForm || normalizedTarget.includes("npra")) {
      await this.deleteNPRAForm();
      return;
    }
    if (this.state.opportunity?.company) {
      await this.deleteCompanyDocument();
      return;
    }
    await this.deleteCustomerDocument();
  };

  setEntityToBeDeleted = (modalTarget: string): void => {
    this.setState({ modalTarget });
  };

  toggleDeleteConfirmationModal = (id?: string): void => {
    this.setState((prevState) => ({
      showDeleteConfirmationModal:
        id !== undefined ? true : !prevState.showDeleteConfirmationModal,
      idToBeDeleted:
        id !== undefined ? id : prevState.showDeleteConfirmationModal ? undefined : prevState.idToBeDeleted,
    }));
  };

  loadChecklist = (): void => {
    const { id: opportunityId } = this.state;
    request({
      query: GET_OPPORTUNITY_CHECKLIST,
      variables: { opportunityId },
    }).then(
      ({
        data: { data },
      }: AxiosResponse<{
        data: {
          onboardingChecklist: {
            percentage: number;
            result: ValidationStatus[];
          };
        };
      }>): void => {
        this.setState({
          checklistData: data.onboardingChecklist,
        });
      },
    );
  };

generateButtonColor = (state: string): string => {
  return STATUS_COLORS[getProgressStatus(state)] ?? "default-btn";
};

  getOpportunity = (): void => {
    const { id: opportunityId } = this.state;
    this.setState({ isLoading: true });
    request({
      query: GET_OPPORTUNITY,
      variables: { opportunityId },
    }).then(
      ({
        data,
      }: AxiosResponse<{
        data: {
          opportunity: Opportunity;
        };
      }>): void => {
        if (data.data.opportunity) {
          if (
            data.data.opportunity.progressStatus === ProgressStatuses.incomplete
          ) {
            this.setState({
              opportunity: data.data.opportunity,
              redirect: true,
            });
            return;
          }
          const opp = data.data.opportunity;
          this.setState({
            opportunity: {
              ...opp,
              contributionAllocationTypes:
                opp.companySubscription?.contributionAllocationTypes ??
                opp.contributionAllocationTypes ??
                [],
            },
            interactions: opp.opportunityInteractions,
            preOpportunityInteractions:
              opp.preOpportunityInteractions,
            isLoading: false,
          });
        } else {
          this.props.history.push("/business-development");
        }
      },
    );
  };

  assignUserToOpportunity = (userId: string): void => {
    const { id, opportunity } = this.state;

    if (opportunity) {
      this.setState({ isLoading: true });
      request({
        query: ASSIGN_USER_TO_OPPORTUNITY,
        variables: {
          opportunityId: id,
          assigneeId: userId,
        },
      }).then(
        ({
          data,
        }: AxiosResponse<{
          data: {
            assignAssigneeToOpportunity: {
              opportunity: Opportunity | null;
            };
          };
        }>): void => {
          this.setState({ isLoading: false });

          if (data.data.assignAssigneeToOpportunity.opportunity) {
            this.setState({
              opportunity: {
                ...opportunity,
                assignee:
                  data.data.assignAssigneeToOpportunity.opportunity.assignee,
              },
            });
          } else {
            message.error("Failed to assign user to opportunity");
          }
        },
      );
    }
  };

  createTicket = async (): Promise<void> => {
    const { interactions, opportunity } = this.state;
    interface Response {
      ticket: Ticket;
    }

    let phoneNumber;
    let firstName;
    let lastName;

    const lastInteraction = interactions
      .sort((a, b) => parseInt(a.id) - parseInt(b.id))
      .pop();

    if (opportunity?.customer) {
      const phones = opportunity.customer.activeCustomerPhones;
      const hasPhones = phones.length > 0;

      phoneNumber = hasPhones ? phones[0].phone : "";
      firstName = opportunity.customer.firstName || "";
      lastName = opportunity.customer.lastName || " ";
    } else if (opportunity?.company) {
      const phones = opportunity.company.companyPhones;
      const hasPhones = phones.length > 0;

      phoneNumber = hasPhones ? phones[0].phone : "";
      firstName = opportunity?.company?.name || "";
      lastName = firstName;
    }

    try {
      const response: AxiosResponse = await request({
        query: CREATE_OPPORTUNITY_TICKET,
        variables: {
          opportunityInteractionId: lastInteraction?.id,
          source: ticketSource,
          properties: {
            group: "Users",
            phoneNumber: phoneNumber,
            subject: lastInteraction?.interactionMode.name,
            description: lastInteraction?.description,
            firstName: firstName,
            lastName: lastName,
          },
        },
      });

      const { err } = responseHandler<Response>(
        response,
        "createOpportunityInteractionTicket",
      );
      if (err) {
        message.error(`Could Not Create Ticket - ${generateDisplayErrorMessage(err)}`);
      }
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  continueOpportunity = (): Promise<AxiosResponse> => {
    const { opportunity } = this.state;
    return request({
      query: CONTINUE_OPPORTUNITY,
      variables: { opportunityId: opportunity && opportunity.id },
    });
  };

  moveToInProgress = async (): Promise<void> => {
    const { opportunity } = this.state;

    interface Response {
      opportunity?: Opportunity | null;
      errors: { property: string; description: string }[];
    }

    try {
      this.setState({ isLoading: true });
      const response: AxiosResponse = await this.continueOpportunity();
      const { err, mutation } = responseHandler<Response>(
        response,
        "continueOpportunity",
      );
      this.setState({ isLoading: false });

      if (err) {
        message.error(`You have to assign a user to the opportunity - ${generateDisplayErrorMessage(err)}`);
      } else if (mutation?.opportunity) {
        this.setState({
          opportunity: { ...opportunity, ...mutation.opportunity },
          isLoading: false,
        });
      }
    } catch (error) {
      message.error(`An error occurred, please try again later - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    }
  };

  assignPetraId = async (): Promise<void> => {
    const { opportunity } = this.state;

    interface Response {
      opportunity?: Opportunity | null;
      errors: { property: string; description: string }[];
    }

    try {
      this.setState({ isLoading: true });
      const response: AxiosResponse = await this.continueOpportunity();
      const { err, mutation } = responseHandler<Response>(
        response,
        "continueOpportunity",
      );
      await this.loadChecklist();
      this.setState({ isLoading: false });

      if (err) {
        message.error(`You have to add at least one interaction - ${generateDisplayErrorMessage(err)}`);
      } else if (mutation?.opportunity) {
        this.setState({
          opportunity: { ...opportunity, ...mutation.opportunity },
          isLoading: false,
        });
      }
    } catch (error) {
      message.error(`An error occurred, please try again later - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    }
  };

  winOpportunity = async (): Promise<void> => {
    const { opportunity } = this.state;

    interface Response {
      opportunity?: Opportunity | null;
      errors: { property: string; description: string }[];
    }

    try {
      this.setState({ isLoading: true });
      const response: AxiosResponse = await this.continueOpportunity();
      const { err, mutation } = responseHandler<Response>(
        response,
        "continueOpportunity",
      );
      this.setState({ isLoading: false, showChecklist: false });

      if (err) {
        message.error(`An error Occured - ${generateDisplayErrorMessage(err)}`);
      } else if (mutation?.opportunity) {
        this.setState({
          opportunity: { ...opportunity, ...mutation.opportunity },
          isLoading: false,
        });
      }
    } catch (error) {
      message.error(`Failed To Complete Opportunity - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    }
  };

  componentDidMount(): void {
    this.setState({ id: this.props.match.params.id }, (): void => {
      this.getOpportunity();
      this.loadChecklist();
      this.getAllUsers();
      this.getCountries();
    });
  }

  openProductEditModal = async (): Promise<void> => {
    const { opportunity } = this.state;
    if (!opportunity) return;

    if (!this.state.petraProducts.length) {
      await this.loadPetraProducts();
    }
    await this.loadAllSalesPeopleForModal();

    if (opportunity.product) {
      this.showCategoryAndTierForModal(opportunity.product.id);
    }
    this.showContributionFieldsForModal(opportunity.contributionType ?? "");

    this.setState({ showProductEditModal: true });
  };

  loadPetraProducts = async (): Promise<void> => {
    try {
      const response = await request({ query: GET_OPPORTUNITY_FORM_DATA });
      const data = response.data?.data;
      if (data?.opportunityProspects && this.state.opportunity) {
        const products = filterProductsByProspect(this.state.opportunity, data.opportunityProspects as OpportunityProspect[]);
        this.setState({ petraProducts: products as Product[] });
      }
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  loadAllSalesPeopleForModal = async (): Promise<void> => {
    try {
      const response = await request({ query: GET_SALES_PEOPLE, variables: { page: 1, q: "" } });
      const results = response.data?.data?.salesPeople?.results ?? [];
      this.setState({ salesPersons: results });
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  handleSalesPersonSearchInputForModal = debounce(async (q: string): Promise<void> => {
    this.setState({ salesPersonSearchTerm: q });
    try {
      const response = await request({ query: GET_SALES_PEOPLE, variables: { page: 1, q } });
      const results = response.data?.data?.salesPeople?.results ?? [];
      this.setState({ salesPersons: results });
    } catch (error) {
      Sentry.captureException(error);
    }
  }, DEBOUNCE_TIME);

  showCategoryAndTierForModal = (productId: string): void => {
    const product = this.state.petraProducts.find((p) => p.id === productId);
    if (!product) {
      this.setState({ productCategoryAndTier: "" });
      return;
    }
    let tier = "Tier 4";
    if (product.productTier.name === "tier_2") tier = "Tier 2";
    else if (product.productTier.name === "tier_3") tier = "Tier 3";
    const categoryTier = `${product.productCategory.name} - ${tier}`;

    const currentValues: string[] =
      this.productEditReference.current?.getFieldValue("contributionAllocationTypes") ?? [];
    const sanitized = sanitizeContributionAllocationTypes(currentValues, product.name);
    if (sanitized.length !== currentValues.length) {
      this.productEditReference.current?.setFieldsValue({ contributionAllocationTypes: sanitized });
    }

    this.setState({ productCategoryAndTier: categoryTier });
  };

  showContributionFieldsForModal = (contributionType: string): void => {
    const contributionState = getContributionAmountFieldState(contributionType);
    this.setState({ productEditFieldsToHide: contributionState.hiddenFields });
  };

  saveProductEdit = async (): Promise<void> => {
    const { opportunity } = this.state;
    if (!opportunity || !this.productEditReference.current) return;

    this.setState({ isProductEditSaving: true });
    try {
      const data = await this.productEditReference.current.validateFields([
        "product",
        "sourceOfAwareness",
        "productPaymentType",
        "contributionType",
        "signupReason",
        "contributionAllocationTypes",
        "salesPersonId",
        "userId",
        "monthlyExpectedContribution",
        "lumpsumExpectedContribution",
      ]);

      const normalizedContributionData = normalizeContributionAmountPayload({
        contributionType: data.contributionType || null,
        monthlyExpectedContribution: data.monthlyExpectedContribution
          ? parseFloat(data.monthlyExpectedContribution)
          : null,
        lumpsumExpectedContribution: data.lumpsumExpectedContribution
          ? parseFloat(data.lumpsumExpectedContribution)
          : null,
      });

      const response: AxiosResponse = await request({
        query: ASSIGN_PRODUCT_TO_OPPORTUNITY,
        variables: {
          opportunityId: opportunity.id,
          productId: data.product,
          sourceOfAwareness: data.sourceOfAwareness || null,
          signupReason: data.signupReason || null,
          productPaymentType: data.productPaymentType || null,
          contributionType: data.contributionType || null,
          contributionAllocationTypes: data.contributionAllocationTypes || [],
          salesPersonId: data.salesPersonId || null,
          monthlyExpectedContribution:
            normalizedContributionData.monthlyExpectedContribution,
          lumpsumExpectedContribution:
            normalizedContributionData.lumpsumExpectedContribution,
        },
      });

      const { err } = responseHandler(response, "assignProductToOpportunity");
      if (err) {
        message.error(generateDisplayErrorMessage(err));
        return;
      }

      if (data.userId && data.userId !== opportunity.assignee?.id) {
        const assignResponse: AxiosResponse = await request({
          query: ASSIGN_USER_TO_OPPORTUNITY,
          variables: { opportunityId: opportunity.id, assigneeId: data.userId },
        });
        const { err: assignErr } = responseHandler(assignResponse, "assignAssigneeToOpportunity");
        if (assignErr) {
          message.error(generateDisplayErrorMessage(assignErr));
          return;
        }
      }

      message.success("Opportunity updated successfully");
      this.setState({ showProductEditModal: false });
      this.getOpportunity();
    } catch (error) {
      handleValidationErrors(error);
    } finally {
      this.setState({ isProductEditSaving: false });
    }
  };

  toggleEditProductPage = async (): Promise<void> => {
    const isEditingProduct = this.state.isEditingProductData;
    this.setState({ isEditingProductData: !isEditingProduct });
  };

  toggleEditingClientData = async (): Promise<void> => {
    const {
      isEditingClientData,
      opportunity,
      titles,
      maritalStatuses,
    } = this.state;

    const customer = opportunity?.customer;
    const company = opportunity?.company;
    if (!isEditingClientData && customer) {
      this.setState({
        isEditingClientData: true,
        isClientEditVerified: !!customer.verificationProofUrl,
        photoIdUrl: customer.photoIdUrl || "",
        ghanaCardIdUrl: customer.ghanaCardIdUrl || "",
        ghanaCardIdBackUrl: customer.ghanaCardIdBackUrl || "",
        signatureUrl: customer.signatureUrl || "",
        verificationProofUrl: customer.verificationProofUrl || "",
      });

      this.clientEditReference.current?.setFieldsValue({
        isVerified: !!customer.verificationProofUrl,
        verificationProofUrl: customer.verificationProofUrl,
      });

      if (!titles.length) {
        this.getQuickEditTitles();
      }
      if (!maritalStatuses.length) {
        this.getQuickEditMaritalStatuses();
      }
      return;
    }

    if (!isEditingClientData && company) {
      this.getIndustrySectors();
      this.getTrusteesSummary();
      this.getAllRelationshipManagers();
      this.setState({ isEditingClientData: true });
      return;
    }

    this.setState({ isEditingClientData: !isEditingClientData });
  };

  getIndustrySectors = async (): Promise<void> => {
    try {
      const response: AxiosResponse = await request({
        query: GET_INDUSTRY_SECTORS,
      });
      const {
        data: { industrySectors },
      } = response.data;
      this.setState({ industrySectors: industrySectors || [] });
    } catch (error) {
      message.error("Failed to load industry sectors");
      Sentry.captureException(error);
    }
  };

  getTrusteesSummary = async (): Promise<void> => {
    try {
      const response: AxiosResponse = await request({
        query: GET_TRUSTEES_SUMMARY,
      });
      const {
        data: { trustees },
      } = response.data;
      this.setState({ trustees: trustees || [] });
    } catch (error) {
      message.error("Failed to load trustees");
      Sentry.captureException(error);
    }
  };

  getAllRelationshipManagers = (): void => {
    request({
      query: GET_ROLE_SUMMARY,
      variables: {
        id: relationshipManagerRoleId,
      },
    }).then((response: AxiosResponse): void => {
      const {
        data: { data },
      } = response;
      this.setState({ relationshipManagers: data.role.users || [] });
    });
  };

  getQuickEditTitles = async (): Promise<void> => {
    interface Response {
      data: {
        titles: Title[];
      };
    }
    try {
      const {
        data: { data },
      }: AxiosResponse<Response> = await request({
        query: GET_TITLES,
      });
      this.setState({ titles: data.titles });
    } catch (error) {
      message.error("Failed to get titles");
      Sentry.captureException(error);
    }
  };

  getQuickEditMaritalStatuses = async (): Promise<void> => {
    interface Response {
      data: {
        maritalStatuses: MaritalStatus[];
      };
    }
    try {
      const {
        data: { data },
      }: AxiosResponse<Response> = await request({
        query: GET_MARITAL_STATUSES,
      });
      this.setState({ maritalStatuses: data.maritalStatuses });
    } catch (error) {
      message.error("Failed to get marital statuses");
      Sentry.captureException(error);
    }
  };

  handleClientEditFileUploadChange = (
    field: string,
    combinedInfo: string,
  ): void => {
    const [url] = combinedInfo.split("@@@");
    this.setState((prevState) => ({
      ...prevState,
      [field]: url,
    }));
  };

  handleClientEditDelete = (file: UploadFile): void => {
    let newState = {};
    switch (file.name) {
      case "Ghana Card":
        newState = { ghanaCardIdUrl: "", photoIdUrl: "" };
        break;
      case "Ghana Card (Back)":
        newState = { ghanaCardIdBackUrl: "" };
        break;
      case "Customer Signature":
        newState = { signatureUrl: "" };
        break;
      case "Verification Proof":
        newState = { verificationProofUrl: "" };
        break;
      default:
        newState = { photoIdUrl: "" };
    }
    this.setState(newState as Pick<
      State,
      | "photoIdUrl"
      | "ghanaCardIdUrl"
      | "ghanaCardIdBackUrl"
      | "signatureUrl"
      | "verificationProofUrl"
    >);
  };

  changeClientEditIsVerified = (checkedValue: boolean): void => {
    this.setState({ isClientEditVerified: checkedValue });
  };

  saveData = (): void => {
    if (this.state.isEditingProductData) {
      this.toggleEditProductPage();
    }
    if (this.state.isEditingClientData) {
      this.toggleEditingClientData();
    }

    this.getOpportunity();
    this.loadChecklist();
  };

  loadOpportunity = async (): Promise<void> => {
    const { opportunity } = this.state;
    this.setState({ isLoading: true });

    try {
      const response: AxiosResponse<{
        data: {
          opportunity: Opportunity;
        };
      }> = await request({
        query: LOAD_OPPORTUNITY_LIGHT,
        variables: { opportunityId: opportunity?.id },
      });

      if (response.data.data.opportunity) {
        this.setState({
          opportunity: { ...opportunity, ...response.data.data.opportunity },
          isLoading: false,
        });
      }
    } catch (e) {
      message.error(`Failed to load opportunity - ${generateDisplayErrorMessage(e)}`);
    }
  };

  updateBeneficiaryInformation = (
    beneficiaryInfo: UpdateBeneficiaryInformationFnProps,
  ): void => {
    const { opportunity } = this.state;

    if (!opportunity?.customer?.customerSubscriptions) return;

    const targetSubscriptionId =
      beneficiaryInfo.subscriptionId ||
      beneficiaryInfo.beneficiaries?.[0]?.customerSubscription?.id;

    if (!targetSubscriptionId) {
      void this.getOpportunity();
      return;
    }

    let matchedTargetSubscription = false;

    const updatedCustomerSubscriptions =
      opportunity.customer.customerSubscriptions.map((subscription) => {
        if (String(targetSubscriptionId) === String(subscription.id)) {
          matchedTargetSubscription = true;
          return {
            ...subscription,
            customerBeneficiaries: beneficiaryInfo.beneficiaries.map((b) => ({
              ...b,
              customerSubscription: subscription,
            })),
          };
        }

        if (
          beneficiaryInfo.previousSubscriptionId &&
          beneficiaryInfo.beneficiaryId &&
          String(beneficiaryInfo.previousSubscriptionId) ===
            String(subscription.id) &&
          String(beneficiaryInfo.previousSubscriptionId) !==
            String(targetSubscriptionId)
        ) {
          return {
            ...subscription,
            customerBeneficiaries: (subscription.customerBeneficiaries || []).filter(
              (beneficiary) => beneficiary.id !== beneficiaryInfo.beneficiaryId,
            ),
          };
        }

        return subscription;
      });

    if (!matchedTargetSubscription) {
      void this.getOpportunity();
      return;
    }

    this.setState({
      opportunity: {
        ...opportunity,
        customer: {
          ...opportunity.customer,
          customerSubscriptions: updatedCustomerSubscriptions,
        },
      },
    });
  };

  updateCompanyPaymentInformation = (
    paymentInfo: UpdatePaymentInformationFnProps,
  ): void => {
    const { opportunity } = this.state;
    if (!opportunity?.company) return;

    const updatedOpportunity: Opportunity = mergeCompanyPaymentInformation(
      opportunity,
      opportunity.company,
      paymentInfo,
    );

    this.setState({ opportunity: updatedOpportunity });
  };

  updateCustomerPaymentInformation = (
    paymentInfo: UpdatePaymentInformationFnProps,
  ): void => {
    const { opportunity } = this.state;
    if (!opportunity?.customer) return;

    const updatedOpportunity: Opportunity = mergeCustomerPaymentInformation(
      opportunity,
      opportunity.customer,
      paymentInfo,
    );

    this.setState({ opportunity: updatedOpportunity });
  };

  openContactModal = (
    type: "email" | "phone" | "address",
    mode: "add" | "edit",
    record?: AssociatedEmail | AssociatedPhone | AssociatedAddress,
  ): void => {
    if (type === "address" && mode === "add") {
      this.setState({ addressType: AssociatedEntityConstants.PhysicalAddressCode });
    }
    if (mode === "edit" && record) {
      if (type === "email") {
        this.setState({
          selectedEmail: record as AssociatedEmail,
        });
      } else if (type === "phone") {
        this.setState({
          selectedPhone: record as AssociatedPhone,
        });
      } else if (type === "address") {
        this.setState({
          selectedAddress: record as AssociatedAddress,
          addressType: (record as AssociatedAddress).address?.isPostalBox
            ? AssociatedEntityConstants.PostalAddressCode
            : AssociatedEntityConstants.PhysicalAddressCode,
        });
      }
    }
    this.resetContactForm(type);
    this.setState({
      showContactModal: true,
      contactModalType: type,
      contactModalMode: mode,
    });
  };

  resetContactForm = (type: "email" | "phone" | "address"): void => {
    const reference =
      type === "email"
        ? this.contactEmailReference
        : type === "phone"
        ? this.contactPhoneReference
        : this.contactAddressReference;

    reference.current?.resetFields();
  };

  closeContactModal = (): void => {
    this.setState({
      showContactModal: false,
      contactModalType: null,
      contactModalMode: "add",
      selectedEmail: null,
      selectedPhone: null,
      selectedAddress: null,
    });
  };

  openBackgroundInfoModal = (mode: "add" | "edit"): void => {
    const backgroundInfo =
      this.state.opportunity?.customer?.customerBackgroundInformation;
    this.setState(
      {
        showBackgroundInfoModal: true,
        backgroundInfoMode: mode,
        fatherDeceased: backgroundInfo?.fatherDeceased || false,
        motherDeceased: backgroundInfo?.motherDeceased || false,
      },
      () => {
        if (backgroundInfo) {
          this.backgroundInfoReference.current?.setFieldsValue({
            fatherName: backgroundInfo.fatherName,
            fatherDeceased: backgroundInfo.fatherDeceased,
            fatherPhoneNumber: backgroundInfo.fatherPhoneNumber,
            motherName: backgroundInfo.motherName,
            motherDeceased: backgroundInfo.motherDeceased,
            motherPhoneNumber: backgroundInfo.motherPhoneNumber,
            previousSsnitNumber: backgroundInfo.previousSsnitNumber,
            hometown: backgroundInfo.hometown,
            nextOfKinName: backgroundInfo.nextOfKinName,
            nextOfKinPhoneNumber: backgroundInfo.nextOfKinPhoneNumber,
            previousEmployer: backgroundInfo.previousEmployer,
          });
        } else {
          this.backgroundInfoReference.current?.resetFields();
        }
      },
    );
  };

  closeBackgroundInfoModal = (): void => {
    this.setState({ showBackgroundInfoModal: false });
  };

  handleBackgroundFatherDeceasedChange = (checkedValue: boolean): void => {
    this.setState({ fatherDeceased: checkedValue });
  };

  handleBackgroundMotherDeceasedChange = (checkedValue: boolean): void => {
    this.setState({ motherDeceased: checkedValue });
  };

  saveBackgroundInfo = async (): Promise<void> => {
    const { opportunity, backgroundInfoMode, fatherDeceased, motherDeceased } =
      this.state;
    const customer = opportunity?.customer;
    if (!customer || !this.backgroundInfoReference.current) return;

    const fieldsToBeValidated = [
      "fatherName",
      "fatherDeceased",
      "fatherPhoneNumber",
      "motherName",
      "motherDeceased",
      "motherPhoneNumber",
      "previousSsnitNumber",
      "hometown",
      "nextOfKinName",
      "nextOfKinPhoneNumber",
      "previousEmployer",
    ];

    let data;
    try {
      data = await this.backgroundInfoReference.current.validateFields(
        fieldsToBeValidated,
      );
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    this.setState({ isBackgroundInfoSaving: true });

    try {
      const response: AxiosResponse = await request({
        query:
          backgroundInfoMode === "edit" && customer.customerBackgroundInformation
            ? UPDATE_CUSTOMER_BACKGROUND_INFORMATION
            : CREATE_CUSTOMER_BACKGROUND_INFORMATION,
        variables: {
          ...data,
          customerId: customer.id,
          fatherDeceased: fatherDeceased || false,
          motherDeceased: motherDeceased || false,
        },
      });

      const mutationName =
        backgroundInfoMode === "edit" && customer.customerBackgroundInformation
          ? "updateCustomerBackgroundInformation"
          : "createCustomerBackgroundInformation";

      const { err, mutation } = responseHandler<{
        customerBackgroundInformation: CustomerBackgroundInformation;
        errors: APIError[];
      }>(response, mutationName);

      if (err) {
        this.backgroundInfoReference.current.setFields(
          err.map(mapAPIErrorToFormError),
        );
        return;
      }

      if (mutation?.customerBackgroundInformation && opportunity) {
        this.setState({
          opportunity: {
            ...opportunity,
            customer: {
              ...customer,
              customerBackgroundInformation:
                mutation.customerBackgroundInformation,
            },
          },
        });
        message.success("Background info saved successfully");
        this.closeBackgroundInfoModal();
      }
    } catch (error) {
      message.error("Failed to save background info");
      Sentry.captureException(error);
    } finally {
      this.setState({ isBackgroundInfoSaving: false });
    }
  };

  openBackgroundInfoDeleteModal = (): void => {
    this.setState({ showBackgroundInfoDeleteModal: true });
  };

  closeBackgroundInfoDeleteModal = (): void => {
    this.setState({ showBackgroundInfoDeleteModal: false });
  };

  deleteBackgroundInfo = async (): Promise<void> => {
    const { opportunity } = this.state;
    const customer = opportunity?.customer;
    if (!customer) return;

    this.setState({ isBackgroundInfoSaving: true });
    try {
      const response: AxiosResponse = await request({
        query: UPDATE_CUSTOMER_BACKGROUND_INFORMATION,
        variables: {
          customerId: customer.id,
          hometown: "",
          previousSsnitNumber: "",
          nextOfKinName: "",
          nextOfKinPhoneNumber: "",
          fatherName: "",
          fatherDeceased: false,
          fatherPhoneNumber: "",
          motherName: "",
          motherDeceased: false,
          motherPhoneNumber: "",
          previousEmployer: "",
        },
      });

      const { err } = responseHandler(
        response,
        "updateCustomerBackgroundInformation",
      );
      if (err) {
        message.error(generateDisplayErrorMessage(err));
        return;
      }

      if (opportunity) {
        this.setState({
          opportunity: {
            ...opportunity,
            customer: {
              ...customer,
              customerBackgroundInformation: undefined,
            },
          },
        });
      }
      message.success("Background info deleted");
      this.closeBackgroundInfoDeleteModal();
    } catch (error) {
      message.error("Failed to delete background info");
      Sentry.captureException(error);
    } finally {
      this.setState({ isBackgroundInfoSaving: false });
    }
  };

  saveContactEmail = async (): Promise<void> => {
    const { opportunity, selectedEmail, contactModalMode } = this.state;
    if (!opportunity || !this.contactEmailReference.current) return;

    const fieldsToBeValidated = ["email"];
    let data;
    try {
      data = await this.contactEmailReference.current.validateFields(
        fieldsToBeValidated,
      );
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    this.setState({ contactModalLoading: true });

    try {
      const response: AxiosResponse = await request({
        query: CREATE_EMAIL,
        variables: { address: data.email },
      });

      const { err, handled, mutation } = responseHandler<{
        email: EmailInterface;
        errors: APIError[];
      }>(response, "createEmail");

      if (handled) {
        message.error("An Error Occurred");
        return;
      } else if (err) {
        this.contactEmailReference.current.setFields(
          err.map(mapAPIErrorToFormError),
        );
        message.error(generateDisplayErrorMessage(err));
        return;
      }

      if (!mutation.email) {
        message.error("Email Creation Failed");
        return;
      }

      if (opportunity.customer) {
        if (contactModalMode === "edit" && selectedEmail) {
          const updateResponse: AxiosResponse = await request({
            query: UPDATE_CUSTOMER_EMAIL,
            variables: {
              customerEmailId: selectedEmail.id,
              customerId: opportunity.customer.id,
              emailId: mutation.email.id,
            },
          });

          const { err: updateErr } = responseHandler(
            updateResponse,
            "updateCustomerEmail",
          );
          if (updateErr) {
            message.error(generateDisplayErrorMessage(updateErr));
            return;
          }
        } else {
          const assignResponse: AxiosResponse = await request({
            query: ASSIGN_EMAIL_TO_CUSTOMER,
            variables: { emailId: mutation.email.id, customerId: opportunity.customer.id },
          });

          const { err: assignErr } = responseHandler(
            assignResponse,
            "assignEmailToCustomer",
          );
          if (assignErr) {
            message.error(generateDisplayErrorMessage(assignErr));
            return;
          }
        }
      } else if (opportunity.company) {
        if (contactModalMode === "edit" && selectedEmail) {
          const updateResponse: AxiosResponse = await request({
            query: UPDATE_COMPANY_EMAIL,
            variables: {
              companyEmailId: selectedEmail.id,
              companyId: opportunity.company.id,
              emailId: mutation.email.id,
            },
          });

          const { err: updateErr } = responseHandler(
            updateResponse,
            "updateCompanyEmail",
          );
          if (updateErr) {
            message.error(generateDisplayErrorMessage(updateErr));
            return;
          }
        } else {
          const assignResponse: AxiosResponse = await request({
            query: ASSIGN_EMAIL_TO_COMPANY,
            variables: { emailId: mutation.email.id, companyId: opportunity.company.id },
          });

          const { err: assignErr } = responseHandler(
            assignResponse,
            "assignEmailToCompany",
          );
          if (assignErr) {
            message.error(generateDisplayErrorMessage(assignErr));
            return;
          }
        }
      } else {
        message.error("No customer or company associated with this opportunity");
        return;
      }

      message.success(
        contactModalMode === "edit" ? "Email updated successfully" : "Email added successfully",
      );
      this.closeContactModal();
      this.getOpportunity();
    } catch (error) {
      message.error("Email action failed");
      Sentry.captureException(error);
    } finally {
      this.setState({ contactModalLoading: false });
    }
  };

  saveContactPhone = async (): Promise<void> => {
    const { opportunity, selectedPhone, contactModalMode } = this.state;
    if (!opportunity || !this.contactPhoneReference.current) return;

    const fieldsToBeValidated = ["phone"];
    let data;
    try {
      data = await this.contactPhoneReference.current.validateFields(
        fieldsToBeValidated,
      );
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    this.setState({ contactModalLoading: true });

    try {
      const response: AxiosResponse = await request({
        query: CREATE_PHONE,
        variables: { number: data.phone },
      });

      const { err, handled, mutation } = responseHandler<{
        phone: PhoneInterface;
        errors: APIError[];
      }>(response, "createPhone");

      if (handled) {
        message.error("An Error Occurred");
        return;
      } else if (err) {
        this.contactPhoneReference.current.setFields(
          err.map(mapAPIErrorToFormError),
        );
        message.error(generateDisplayErrorMessage(err));
        return;
      }

      if (!mutation.phone) {
        message.error("Phone Creation Failed");
        return;
      }

      if (opportunity.customer) {
        if (contactModalMode === "edit" && selectedPhone) {
          const updateResponse: AxiosResponse = await request({
            query: UPDATE_CUSTOMER_PHONE,
            variables: {
              customerPhoneId: selectedPhone.id,
              customerId: opportunity.customer.id,
              phoneId: mutation.phone.id,
            },
          });

          const { err: updateErr } = responseHandler(
            updateResponse,
            "updateCustomerPhone",
          );
          if (updateErr) {
            message.error(generateDisplayErrorMessage(updateErr));
            return;
          }
        } else {
          const assignResponse: AxiosResponse = await request({
            query: ASSIGN_PHONE_TO_CUSTOMER,
            variables: { phoneId: mutation.phone.id, customerId: opportunity.customer.id },
          });

          const { err: assignErr } = responseHandler(
            assignResponse,
            "assignPhoneToCustomer",
          );
          if (assignErr) {
            message.error(generateDisplayErrorMessage(assignErr));
            return;
          }
        }
      } else if (opportunity.company) {
        if (contactModalMode === "edit" && selectedPhone) {
          const updateResponse: AxiosResponse = await request({
            query: UPDATE_COMPANY_PHONE,
            variables: {
              companyPhoneId: selectedPhone.id,
              companyId: opportunity.company.id,
              phoneId: mutation.phone.id,
            },
          });

          const { err: updateErr } = responseHandler(
            updateResponse,
            "updateCompanyPhone",
          );
          if (updateErr) {
            message.error(generateDisplayErrorMessage(updateErr));
            return;
          }
        } else {
          const assignResponse: AxiosResponse = await request({
            query: ASSIGN_PHONE_TO_COMPANY,
            variables: { phoneId: mutation.phone.id, companyId: opportunity.company.id },
          });

          const { err: assignErr } = responseHandler(
            assignResponse,
            "assignPhoneToCompany",
          );
          if (assignErr) {
            message.error(generateDisplayErrorMessage(assignErr));
            return;
          }
        }
      } else {
        message.error("No customer or company associated with this opportunity");
        return;
      }

      message.success(
        contactModalMode === "edit" ? "Phone updated successfully" : "Phone added successfully",
      );
      this.closeContactModal();
      this.getOpportunity();
    } catch (error) {
      message.error("Phone action failed");
      Sentry.captureException(error);
    } finally {
      this.setState({ contactModalLoading: false });
    }
  };

  saveContactAddress = async (): Promise<void> => {
    const { opportunity, selectedAddress, contactModalMode, addressType } =
      this.state;
    if (!opportunity || !this.contactAddressReference.current) return;

    const fieldsToBeValidated = [
      "streetName",
      "poBox",
      "city",
      "addressCountry",
      "addressType",
      "ghPost",
      "region",
    ];

    let data;
    try {
      data = await this.contactAddressReference.current.validateFields(
        fieldsToBeValidated,
      );
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    this.setState({ contactModalLoading: true });

    try {
      const response: AxiosResponse = await request({
        query: CREATE_ADDRESS,
        variables: {
          streetName: data.streetName,
          poBox: data.poBox,
          city: data.city,
          region: data.region,
          addressCountry: data.addressCountry,
          isPostalBox: addressType === AssociatedEntityConstants.PostalAddressCode,
          ghPost: data.ghPost?.length > 0 ? data.ghPost : undefined,
        },
      });

      const { err, handled, mutation } = responseHandler<{
        address: AddressInterface;
        errors: APIError[];
      }>(response, "createAddress");

      if (handled) {
        message.error("An Error Occurred");
        return;
      } else if (err) {
        this.contactAddressReference.current.setFields(
          err.map(mapAPIErrorToFormError),
        );
        message.error(generateDisplayErrorMessage(err));
        return;
      }

      if (!mutation.address) {
        message.error("Address Creation Failed");
        return;
      }

      if (opportunity.customer) {
        if (contactModalMode === "edit" && selectedAddress) {
          const updateResponse: AxiosResponse = await request({
            query: UPDATE_CUSTOMER_ADDRESS,
            variables: {
              customerAddressId: selectedAddress.id,
              customerId: opportunity.customer.id,
              addressId: mutation.address.id,
            },
          });

          const { err: updateErr } = responseHandler(
            updateResponse,
            "updateCustomerAddress",
          );
          if (updateErr) {
            message.error(generateDisplayErrorMessage(updateErr));
            return;
          }
        } else {
          const assignResponse: AxiosResponse = await request({
            query: ASSIGN_ADDRESS_TO_CUSTOMER,
            variables: { addressId: mutation.address.id, customerId: opportunity.customer.id },
          });

          const { err: assignErr } = responseHandler(
            assignResponse,
            "assignAddressToCustomer",
          );
          if (assignErr) {
            message.error(generateDisplayErrorMessage(assignErr));
            return;
          }
        }
      } else if (opportunity.company) {
        if (contactModalMode === "edit" && selectedAddress) {
          const updateResponse: AxiosResponse = await request({
            query: UPDATE_COMPANY_ADDRESS,
            variables: {
              companyAddressId: selectedAddress.id,
              companyId: opportunity.company.id,
              addressId: mutation.address.id,
            },
          });

          const { err: updateErr } = responseHandler(
            updateResponse,
            "updateCompanyAddress",
          );
          if (updateErr) {
            message.error(generateDisplayErrorMessage(updateErr));
            return;
          }
        } else {
          const assignResponse: AxiosResponse = await request({
            query: ASSIGN_ADDRESS_TO_COMPANY,
            variables: { addressId: mutation.address.id, companyId: opportunity.company.id },
          });

          const { err: assignErr } = responseHandler(
            assignResponse,
            "assignAddressToCompany",
          );
          if (assignErr) {
            message.error(generateDisplayErrorMessage(assignErr));
            return;
          }
        }
      } else {
        message.error("No customer or company associated with this opportunity");
        return;
      }

      message.success(
        contactModalMode === "edit" ? "Address updated successfully" : "Address added successfully",
      );
      this.closeContactModal();
      this.getOpportunity();
    } catch (error) {
      message.error("Address action failed");
      Sentry.captureException(error);
    } finally {
      this.setState({ contactModalLoading: false });
    }
  };

  confirmDeleteContact = async (): Promise<void> => {
    const { opportunity, deleteContactType, deleteContactId } = this.state;
    if (!opportunity || !deleteContactType || !deleteContactId) return;

    this.setState({ contactModalLoading: true });
    try {
      const isCustomer = !!opportunity.customer;
      let query = "";
      let mutationName = "";
      let variables: Record<string, string> = {};

      if (deleteContactType === "email") {
        query = isCustomer ? UNASSIGN_EMAIL_FROM_CUSTOMER : UNASSIGN_EMAIL_FROM_COMPANY;
        mutationName = isCustomer
          ? "unassignEmailFromCustomer"
          : "unassignEmailFromCompany";
        variables = isCustomer
          ? { customerEmailId: deleteContactId }
          : { companyEmailId: deleteContactId };
      } else if (deleteContactType === "phone") {
        query = isCustomer
          ? UNASSIGN_PHONE_FROM_CUSTOMER
          : UNASSIGN_PHONE_FROM_COMPANY;
        mutationName = isCustomer
          ? "unassignPhoneFromCustomer"
          : "unassignPhoneFromCompany";
        variables = isCustomer
          ? { customerPhoneId: deleteContactId }
          : { companyPhoneId: deleteContactId };
      } else {
        query = isCustomer
          ? UNASSIGN_ADDRESS_FROM_CUSTOMER
          : UNASSIGN_ADDRESS_FROM_COMPANY;
        mutationName = isCustomer
          ? "unassignAddressFromCustomer"
          : "unassignAddressFromCompany";
        variables = isCustomer
          ? { customerAddressId: deleteContactId }
          : { companyAddressId: deleteContactId };
      }

      const response = await request({
        query,
        variables,
      });

      const { err } = responseHandler(response, mutationName);
      if (err) {
        message.error(generateDisplayErrorMessage(err));
        return;
      }
      message.success("Deleted successfully");
      this.closeDeleteContactModal();
      this.getOpportunity();
    } catch (error) {
      message.error("Delete failed");
      Sentry.captureException(error);
    } finally {
      this.setState({ contactModalLoading: false });
    }
  };

  markEmailAsPrimary = async (email: AssociatedEmail): Promise<void> => {
    const { opportunity } = this.state;
    if (!opportunity) return;

    this.setState({ contactModalLoading: true });

    try {
      const isCustomer = !!opportunity.customer;
      const query = isCustomer ? MARK_EMAIL_AS_PRIMARY : MARK_COMPANY_EMAIL_AS_PRIMARY;
      const mutationName = isCustomer
        ? "markCustomerEmailAsPrimary"
        : "markCompanyEmailAsPrimary";

      const response = await request({
        query,
        variables: { emailId: email.id },
      });
      const { err } = responseHandler(response, mutationName);
      if (err) {
        message.error(generateDisplayErrorMessage(err));
        return;
      }
      message.success("Email marked as primary");
      this.getOpportunity();
    } catch (error) {
      message.error("Failed to mark email as primary");
      Sentry.captureException(error);
    } finally {
      this.setState({ contactModalLoading: false });
    }
  };

  markPhoneAsPrimary = async (phone: AssociatedPhone): Promise<void> => {
    const { opportunity } = this.state;
    if (!opportunity) return;

    this.setState({ contactModalLoading: true });

    try {
      const isCustomer = !!opportunity.customer;
      const query = isCustomer ? MARK_PHONE_AS_PRIMARY : MARK_COMPANY_PHONE_AS_PRIMARY;
      const mutationName = isCustomer
        ? "markCustomerPhoneAsPrimary"
        : "markCompanyPhoneAsPrimary";

      const response = await request({
        query,
        variables: { phoneId: phone.id },
      });
      const { err } = responseHandler(response, mutationName);
      if (err) {
        message.error(generateDisplayErrorMessage(err));
        return;
      }
      message.success("Phone marked as primary");
      this.getOpportunity();
    } catch (error) {
      message.error("Failed to mark phone as primary");
      Sentry.captureException(error);
    } finally {
      this.setState({ contactModalLoading: false });
    }
  };

  markAddressAsPrimary = async (address: AssociatedAddress): Promise<void> => {
    const { opportunity } = this.state;
    if (!opportunity) return;

    this.setState({ contactModalLoading: true });

    try {
      const isCustomer = !!opportunity.customer;

      const query = isCustomer ? MARK_ADDRESS_AS_PRIMARY : MARK_COMPANY_ADDRESS_AS_PRIMARY;
      const mutationName = isCustomer
        ? "markCustomerAddressAsPrimary"
        : "markCompanyAddressAsPrimary";

      const response = await request({
        query,
        variables: { addressId: address.id },
      });
      const { err } = responseHandler(response, mutationName);
      if (err) {
        message.error(generateDisplayErrorMessage(err));
        return;
      }
      message.success("Address marked as primary");
      this.getOpportunity();
    } catch (error) {
      message.error("Failed to mark address as primary");
      Sentry.captureException(error);
    } finally {
      this.setState({ contactModalLoading: false });
    }
  };

  reactivateEmail = async (email: AssociatedEmail): Promise<void> => {
    const { opportunity } = this.state;
    if (!opportunity) return;

    this.setState({ contactModalLoading: true });

    try {
      const isCustomer = !!opportunity.customer;
      const query = isCustomer ? ASSIGN_EMAIL_TO_CUSTOMER : ASSIGN_EMAIL_TO_COMPANY;
      const mutationName = isCustomer ? "assignEmailToCustomer" : "assignEmailToCompany";
      const variables = isCustomer
        ? { emailId: email.email.id, customerId: opportunity.customer?.id }
        : { emailId: email.email.id, companyId: opportunity.company?.id };

      const response = await request({
        query,
        variables,
      });
      const { err } = responseHandler(response, mutationName);
      if (err) {
        message.error(generateDisplayErrorMessage(err));
        return;
      }

      message.success("Email reactivated");
      this.getOpportunity();
    } catch (error) {
      message.error("Failed to reactivate email");
      Sentry.captureException(error);
    } finally {
      this.setState({ contactModalLoading: false });
    }
  };

  reactivatePhone = async (phone: AssociatedPhone): Promise<void> => {
    const { opportunity } = this.state;
    if (!opportunity) return;

    this.setState({ contactModalLoading: true });

    try {
      const isCustomer = !!opportunity.customer;
      const query = isCustomer ? ASSIGN_PHONE_TO_CUSTOMER : ASSIGN_PHONE_TO_COMPANY;
      const mutationName = isCustomer ? "assignPhoneToCustomer" : "assignPhoneToCompany";
      const variables = isCustomer
        ? { phoneId: phone.phone.id, customerId: opportunity.customer?.id }
        : { phoneId: phone.phone.id, companyId: opportunity.company?.id };

      const response = await request({
        query,
        variables,
      });
      const { err } = responseHandler(response, mutationName);
      if (err) {
        message.error(generateDisplayErrorMessage(err));
        return;
      }

      message.success("Phone reactivated");
      this.getOpportunity();
    } catch (error) {
      message.error("Failed to reactivate phone");
      Sentry.captureException(error);
    } finally {
      this.setState({ contactModalLoading: false });
    }
  };

  reactivateAddress = async (address: AssociatedAddress): Promise<void> => {
    const { opportunity } = this.state;
    if (!opportunity) return;

    this.setState({ contactModalLoading: true });

    try {
      const isCustomer = !!opportunity.customer;
      const query = isCustomer ? ASSIGN_ADDRESS_TO_CUSTOMER : ASSIGN_ADDRESS_TO_COMPANY;
      const mutationName = isCustomer
        ? "assignAddressToCustomer"
        : "assignAddressToCompany";
      const variables = isCustomer
        ? { addressId: address.address.id, customerId: opportunity.customer?.id }
        : { addressId: address.address.id, companyId: opportunity.company?.id };

      const response = await request({
        query,
        variables,
      });
      const { err } = responseHandler(response, mutationName);
      if (err) {
        message.error(generateDisplayErrorMessage(err));
        return;
      }

      message.success("Address reactivated");
      this.getOpportunity();
    } catch (error) {
      message.error("Failed to reactivate address");
      Sentry.captureException(error);
    } finally {
      this.setState({ contactModalLoading: false });
    }
  };

  openDeleteContactModal = (
    type: "email" | "phone" | "address",
    id: string,
  ): void => {
    this.setState({
      showContactDeleteModal: true,
      deleteContactType: type,
      deleteContactId: id,
    });
  };

  closeDeleteContactModal = (): void => {
    this.setState({
      showContactDeleteModal: false,
      deleteContactType: null,
      deleteContactId: null,
    });
  };

  handleAddressTypeChange = (value: string): void => {
    this.setState({ addressType: value });
  };

  toggleModal = (): void => {
    if (!this.state.showChecklist) {
      this.loadChecklist();
    }
    this.setState({
      showChecklist: !this.state.showChecklist,
      isLoading: false,
    });
  };

  getTitleBasedOnDrawerContent = (): React.ReactNode => {
    const { activeDrawerContent } = this.state;
    switch (activeDrawerContent) {
      case "audits":
        return "Opportunity Change Logs";
      case "notifierRecords":
        return "Notification Records";
      default:
        return "Entity";
    }
  };

  toggleCancelModal = (): void => {
    this.setState({
      showCancelModal: !this.state.showCancelModal,
    });
  };

  getDrawerContent = (): JSX.Element => {
    const { activeDrawerContent, isFiltering, filteredOpportunityChangeLogs, opportunityChangeLogs } =
      this.state;

    switch (activeDrawerContent) {
      case "audits":
        return (
          <Fragment>
            <div
              style={{
                marginBottom: "20px",
              }}
            >
              <Input.Search
                size="middle"
                type="text"
                placeholder="Filter by ..."
                onChange={this.filterLogs}
              />
            </div>

            {isFiltering
              ? ChangelogTableFactory(filteredOpportunityChangeLogs)
              : ChangelogTableFactory(opportunityChangeLogs)}
          </Fragment>
        )
      case "notifierRecords":
        const records = this.state.opportunity
          ?.notifierRecords as NotificationRecord[];

        return (
          <Fragment>
            {NotificationRecordTableFactory(records, notificationColumns)}
          </Fragment>
        );
      default:
        return <Fragment />;
    }
  };

  getOpportunityChangeLog = async () => {
    const { id } = this.state;
    this.setState({ loadingChangelog: true });
    interface Response {
      data: {
        opportunityChangeLogs: Changelog[];
      };
    }

    try {
      const { data }: AxiosResponse<Response> = await request({
        query: GET_OPPORTUNITY_CHANGELOG,
        variables: {
          opportunityId: id,
        },
      });

      this.setState({
        opportunityChangeLogs: data.data.opportunityChangeLogs,
      });
    } catch (error) {
      message.error(`Failed to fetch changelogs - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    }

    this.setState({ loadingChangelog: false });
  };

  getOpportunityNotifierRecords = (): void => {
    const { id: opportunityId } = this.state;
    this.setState({ isLoading: true });
    request({
      query: GET_OPPORTUNITY_NOTIFIER_RECORDS_QUERY,
      variables: { opportunityId },
    })
      .then(
        ({
          data,
        }: AxiosResponse<{
          data: {
            opportunity: Opportunity;
          };
        }>): void => {
          this.setState({
            opportunity: {
              ...this.state.opportunity,
              ...data.data.opportunity,
            },
          });
        },
      )
      .finally((): void => {
        this.setState({
          isLoading: false,
        });
      });
  };


  executeDrawerQuery(currentDrawer: ActiveDrawerContent): void {
    switch (currentDrawer) {
      case "notifierRecords":
        this.getOpportunityNotifierRecords();
        break;
      case "audits":
        this.getOpportunityChangeLog();
        break;
      default:
        break;
    }
    return;
  }

  toggleDrawer = (activeDrawerContent: ActiveDrawerContent): (() => void) => {
    return (): void => {
      this.executeDrawerQuery(activeDrawerContent);
      this.setState({
        activeDrawerContent,
        showDrawer: !this.state.showDrawer,
        showCancelModal: false,
      });
    };
  };

  toggleDelete = (): void => {
    const prevState = this.state.showDialog;
    this.setState({ showDialog: !prevState });
  };

  deleteOpportunity = async (): Promise<void> => {
    const { id } = this.state;
    this.setState({ isLoading: true });

    try {
      const response: AxiosResponse<{
        data: {
          deleteOpportunity: {
            result: boolean;
          };
          errors: { property: string; description: string }[];
        };
      }> = await request({
        query: DELETE_OPPORTUNITY,
        variables: { id },
      });

      if (
        !response.data.data.errors &&
        response.data.data.deleteOpportunity.result
      ) {
        message.success("Opportunity deleted successfuly");
        this.props.history.push("/business-development");
      } else {
        message.error("Failed to delete opportunity");
      }
      this.setState({ isLoading: false, showDialog: false });
    } catch (error) {
      this.setState({ isLoading: false, showDialog: false });
      message.error(`Failed to delete opportunity - ${generateDisplayErrorMessage(error)}`);
    }
  };

  filterLogs = (e: React.ChangeEvent<HTMLInputElement>): void => {
    e.persist();
    const matches: Changelog[] = [];
    const alreadyMatched: { [x: string]: boolean } = {};
    const filterBy: string = e.target.value.toLowerCase();
    if (filterBy.length === 0) {
      return this.setState({
        filterBy,
        isFiltering: false,
        filteredOpportunityChangeLogs: [],
      });
    }

    const parts = filterBy.split(" ");
    for (const log of this.state.opportunityChangeLogs) {
      for (const part of parts) {
        if (
          !alreadyMatched[log.auditItem] &&
          part !== "" &&
          `${log.auditItem.toLowerCase()} ${log.createdAt}
            .toLowerCase()}`.includes(part)
        ) {
          matches.push(log);
          alreadyMatched[log.auditItem] = true;
        }
      }
    }

    this.setState({
      filterBy,
      isFiltering: true,
      filteredOpportunityChangeLogs: matches,
    });
  };

  cancelEdit = () => {
    this.setState({
      isEditingClientData: false,
      isEditingProductData: false,
    });
  };

  saveClientProfileEdit = async (): Promise<void> => {
    const { opportunity, isClientEditVerified } = this.state;
    if (!opportunity || !this.clientEditReference.current) return;

    this.setState({ isClientEditSaving: true });
    try {
      if (opportunity.customer) {
        if (isClientEditVerified && !this.state.verificationProofUrl) {
          message.error(
            "Proof of Verification is required when marking as verified",
          );
          this.clientEditReference.current.setFields([
            {
              name: "verificationProofUrl",
              errors: ["Please attach proof of verification"],
            },
          ]);
          return;
        }

        const data = await this.clientEditReference.current.validateFields([
          "titleId",
          "firstName",
          "middleName",
          "lastName",
          "ssnitNumber",
          "dateOfBirth",
          "maritalStatusId",
          "gender",
          "countryId",
          "photoIdNumber",
          "photoIdUrl",
          "ghanaCardIdUrl",
          "ghanaCardIdBackUrl",
          "ghanaCardNo",
          "idIssueDate",
          "idExpiryDate",
          "signatureUrl",
          "isVerified",
          "verificationProofUrl",
        ]);

        const response: AxiosResponse = await request({
          query: UPDATE_CUSTOMER_MUTATION,
          variables: {
            customerId: opportunity.customer.id,
            titleId: data.titleId || null,
            firstName: data.firstName,
            middleName: data.middleName || null,
            lastName: data.lastName,
            ssnitNumber: data.ssnitNumber || null,
            maritalStatusId: data.maritalStatusId || null,
            gender: data.gender || opportunity.customer.gender,
            countryId: data.countryId || opportunity.customer.country?.id,
            dateOfBirth: data.dateOfBirth
              ? moment(data.dateOfBirth).format("YYYY-MM-DD")
              : opportunity.customer.dateOfBirth || null,
            photoIdNumber: data.photoIdNumber || null,
            photoIdUrl: this.state.photoIdUrl,
            ghanaCardIdUrl: this.state.ghanaCardIdUrl,
            ghanaCardIdBackUrl: this.state.ghanaCardIdBackUrl,
            ghanaCardNo: data.ghanaCardNo || opportunity.customer.ghanaCardNo || null,
            idIssueDate: data.idIssueDate
              ? moment(data.idIssueDate).format("YYYY-MM-DD")
              : opportunity.customer.idIssueDate || null,
            idExpiryDate: data.idExpiryDate
              ? moment(data.idExpiryDate).format("YYYY-MM-DD")
              : opportunity.customer.idExpiryDate || null,
            signatureUrl: this.state.signatureUrl,
            isVerified:
              this.state.isClientEditVerified ||
              data.isVerified ||
              !!opportunity.customer.verificationProofUrl,
            verificationProofUrl: this.state.verificationProofUrl,
          },
        });

        const { err } = responseHandler(response, "updateCustomer");
        if (err) {
          message.error(generateDisplayErrorMessage(err));
          return;
        }

        message.success("Customer updated successfully");
      } else if (opportunity.company) {
        const data = await this.clientEditReference.current.validateFields([
          "name",
          "tinNumber",
          "industrySectorId",
          "numberOfEmployees",
          "trusteeId",
          "relationshipManagerId",
          "ssnitNumber",
          "rocNumber",
          "dateOfEstablishment",
          "companySegment",
        ]);

        const response: AxiosResponse = await request({
          query: UPDATE_COMPANY_MUTATION,
          variables: {
            companyId: opportunity.company.id,
            name: data.name,
            tinNumber: data.tinNumber || null,
            industrySectorId: data.industrySectorId || null,
            numberOfEmployees:
              data.numberOfEmployees !== undefined &&
              data.numberOfEmployees !== null
                ? Number(data.numberOfEmployees)
                : null,
            trusteeId: data.trusteeId || null,
            relationshipManagerId: data.relationshipManagerId || null,
            ssnitNumber: data.ssnitNumber || null,
            rocNumber: data.rocNumber || null,
            dateOfEstablishment: data.dateOfEstablishment
              ? moment(data.dateOfEstablishment).format("YYYY-MM-DD")
              : null,
            companySegment: data.companySegment || null,
          },
        });

        const { err } = responseHandler(response, "updateCompany");
        if (err) {
          message.error(generateDisplayErrorMessage(err));
          return;
        }

        message.success("Company updated successfully");
      }

      this.cancelEdit();
      this.getOpportunity();
    } catch (error) {
      handleValidationErrors(error);
    } finally {
      this.setState({ isClientEditSaving: false });
    }
  };

  render(): JSX.Element | null {
    const {
      isLoading,
      opportunity,
      users,
      isEditingClientData,
      countries,
      isEditingProductData,
      checklistData,
      showDialog,
      redirect,
      showDrawer,
      contactLevels,
      customers,
      isExistingCustomer,
      companyContactFieldsToHide,
      currentCompanyContact,
      showBackgroundInfoModal,
      showBackgroundInfoDeleteModal,
      backgroundInfoMode,
      isBackgroundInfoSaving,
      isClientEditSaving,
      titles,
      maritalStatuses,
      industrySectors,
      trustees,
      relationshipManagers,
      isClientEditVerified,
      fatherDeceased,
      motherDeceased,
      showProductEditModal,
      isProductEditSaving,
      productCategoryAndTier,
      salesPersons,
      petraProducts,
      productEditFieldsToHide,
    } = this.state;

    if (opportunity && redirect) {
      return (
        <Redirect
          to={`/business-development/new-opportunity?opportunityId=${opportunity?.id}`}
        />
      );
    }

    let showInitiateOnboardingButton = false;
    let showMoveToInprogressButton = false;
    let showAssignPetraIdButton = false;

    if (opportunity) {
      const name = opportunity.progressStatus;

      if (name === ProgressStatuses.lead) {
        showMoveToInprogressButton = true;
      } else if (name === ProgressStatuses.inProgress) {
        showAssignPetraIdButton = true;
      } else if (
        name === ProgressStatuses.pendingMatch ||
        name === ProgressStatuses.onboardingInitated
      ) {
        showInitiateOnboardingButton = true;
      }
    }

    const opportunityWon = opportunity?.state === "won";

    // Once an opportunity is won, client data becomes read-only on this page;
    // components hide their action buttons when handlers are not provided.
    const ifNotWon = <T,>(handler: T): T | undefined =>
      opportunityWon ? undefined : handler;

    const clientDetailsEditButton = !opportunityWon ? (
      <EditButton handleClick={this.toggleEditingClientData} />
    ) : (
      <Fragment />
    );

    const productDataEditButton = !opportunityWon ? (
      <EditButton handleClick={this.openProductEditModal} />
    ) : (
      <Fragment />
    );

    const usersPlusAssignee = getUniqueArrayObjects(
      opportunity?.assignee
        ? [
          ...users,
          { id: opportunity.assignee.id, name: opportunity.assignee.name },
        ]
        : users,
    );

    let contactLevelsIds: string[] = [];
    if (
      currentCompanyContact &&
      (currentCompanyContact.contactLevels?.length ?? 0) > 0
    ) {
      contactLevelsIds =
        currentCompanyContact.contactLevels?.map((level) => level.id) ?? [];
    }

    const isExistingCustomerValue =
      currentCompanyContact?.customer ? true : isExistingCustomer;
    const documentColSpan = isExistingCustomerValue ? 11 : 24;
    const documentDefaultFileList = currentCompanyContact?.documentUrl
      ? [
          {
            uid: `${currentCompanyContact.id}-document`,
            name: extractFileName(currentCompanyContact.documentUrl),
            url: currentCompanyContact.documentUrl,
          },
        ]
      : [];
    const customerOptionsSource = currentCompanyContact?.customer
      ? [
          currentCompanyContact.customer,
          ...customers.filter(
            (customer) => customer.id !== currentCompanyContact.customer?.id,
          ),
        ]
      : customers;

    const opportunityRequiredDocuments: FileUpload[] = (() => {
      if (!opportunity?.company) return [];

      // Company-scoped uploads carry no opportunityId and are not counted by
      // the onboarding checklist, so they are not listed here.
      const docs: FileUpload[] = [];

      const add = (
        document: { id?: string; url?: string } | undefined,
        fileType: string,
      ): void => {
        if (!document?.url) return;
        docs.push({ id: document.id || fileType, fileType, url: document.url });
      };

      add(opportunity.employeeDatasheet, FileTypes.employeeDatasheet);
      add(opportunity.signedApplicationForm, FileTypes.signedApplicationForm);
      add(opportunity.npraForm, FileTypes.npraForm);
      add(opportunity.vestingRuleDocument, FileTypes.vestingRuleDocument);

      return docs;
    })();

    const companyContactPersonFormFields: FormFieldConfig[] = [
      {
        id: "contactName",
        name: "contactName",
        inputType: "string",
        rule: "string",
        placeholder: "Contact Name",
        label: "Contact Name",
        required: true,
        initialValue: currentCompanyContact?.contactName,
        colConfig: {
          span: 11,
        },
      },
      {
        id: "emailAddress",
        name: "emailAddress",
        inputType: "string",
        rule: "string",
        placeholder: "Email Address",
        label: "Email Address",
        required: true,
        initialValue: currentCompanyContact?.emailAddress,
        colConfig: {
          span: 11,
          offset: 1,
        },
      },
      {
        id: "mobileNumber",
        name: "mobileNumber",
        inputType: "string",
        rule: "string",
        placeholder: "Mobile Number",
        label: "Mobile Number",
        required: true,
        initialValue: currentCompanyContact?.mobileNumber,
        colConfig: {
          span: 11,
        },
      },
      {
        id: "contactLevelIds",
        name: "contactLevelIds",
        inputType: "multipleSelect",
        rule: "string",
        placeholder: "Contact Level",
        label: "Contact Level",
        selectOptions: contactLevels,
        isArrayField: true,
        required: true,
        selectHandler: this.handleContactLevelsChange,
        initialValue: contactLevelsIds,
        colConfig: {
          span: 11,
          offset: 1,
        },
      },
      {
        id: "role",
        name: "role",
        inputType: "string",
        rule: "string",
        placeholder: "Role",
        label: "Role",
        required: true,
        initialValue: currentCompanyContact?.role,
        colConfig: {
          span: 11,
        },
      },
      {
        id: "isExistingCustomer",
        name: "isExistingCustomer",
        inputType: "checkbox",
        required: false,
        rule: "boolean",
        label: "Is Existing Customer ?",
        checkboxChangeHandler: (e) =>
          this.handleIsExistingCustomerchecked(e.target.checked),
        checkboxCheckedValue: isExistingCustomerValue,
        colConfig: {
          span: 11,
          offset: 1,
        },
      },
      {
        id: "documentUrl",
        name: "documentUrl",
        label: "Upload Document",
        inputType: "fileUpload",
        defaultFileList: documentDefaultFileList,
        placeholder: "Upload Document",
        rule: "string",
        required: false,
        changeHandler: (combinedInfo: string) =>
          this.handleFileUploadChange("documentUrl", combinedInfo),
        colConfig: {
          span: documentColSpan,
        },
      },
      {
        id: "customerId",
        name: "customerId",
        inputType: "search",
        required: true,
        placeholder: "Seach by name or petraId",
        rule: "string",
        label: "Customer",
        selectOptions: convertCustomersToSelectOptions(customerOptionsSource),
        changeHandler: this.handleCustomerSearchInput,
        onFocus: () => {
          if (this.state.isExistingCustomer && !this.state.customers.length) {
            void this.triggerCustomerSearch();
          }
        },
        initialValue: currentCompanyContact?.customer?.id,
        colConfig: {
          span: 11,
          offset: isExistingCustomerValue ? 1 : 0,
        },
      },
    ];

    const backgroundInfoFields = createBackgroundInfoFields({
      handleFatherDeceasedChange: this.handleBackgroundFatherDeceasedChange,
      handleMotherDeceasedChange: this.handleBackgroundMotherDeceasedChange,
      createBackgroundInfo: this.saveBackgroundInfo,
      updateBackgroundInfo: this.saveBackgroundInfo,
      initialValue:
        opportunity?.customer?.customerBackgroundInformation || undefined,
      fatherDeceased,
      motherDeceased,
    }).filter(
      (field) =>
        field.inputType !== "button" && field.inputType !== "buttonGroup",
    );

    const quickEditFileList =
      opportunity?.customer?.photoIdUrl && opportunity?.customer?.idType
        ? [
            {
              size: 1000,
              type: opportunity.customer.idType.name,
              uid: "1",
              url: opportunity.customer.photoIdUrl,
              name: opportunity.customer.idType.name,
              thumbUrl: opportunity.customer.photoIdUrl,
            },
          ]
        : [];

    const quickEditSignatureUpload = opportunity?.customer?.signatureUrl
      ? [
          {
            size: 1000,
            type: opportunity.customer.signatureUrl,
            uid: "1",
            url: opportunity.customer.signatureUrl,
            name: "Customer Signature",
            thumbUrl: opportunity.customer.signatureUrl,
          },
        ]
      : [];

    const quickEditGhanaCardFileList =
      opportunity?.customer?.ghanaCardNo && opportunity?.customer?.ghanaCardIdUrl
        ? [
            {
              size: 1000,
              type: "Ghana Card",
              uid: "1",
              url: opportunity.customer.ghanaCardIdUrl,
              name: "Ghana Card",
              thumbUrl: opportunity.customer.ghanaCardIdUrl,
            },
          ]
        : [];

    const quickEditGhanaCardBackFileList =
      opportunity?.customer?.ghanaCardNo &&
      opportunity?.customer?.ghanaCardIdBackUrl
        ? [
            {
              size: 1000,
              type: "Ghana Card (Back)",
              uid: "1",
              url: opportunity.customer.ghanaCardIdBackUrl,
              name: "Ghana Card (Back)",
              thumbUrl: opportunity.customer.ghanaCardIdBackUrl,
            },
          ]
        : [];

    const customerClientEditFields: FormFieldConfig[] = opportunity?.customer
      ? [
          {
            id: "titleId",
            name: "titleId",
            inputType: "select",
            rule: "string",
            required: false,
            placeholder: "Title",
            label: "Title",
            selectOptions: titles,
            initialValue: opportunity.customer.title
              ? opportunity.customer.title.id
              : undefined,
            colConfig: {
              span: 7,
            },
          },
          {
            id: "firstName",
            name: "firstName",
            inputType: "string",
            rule: "string",
            placeholder: "First Name",
            label: "First Name",
            initialValue: opportunity.customer.firstName,
            required: true,
            colConfig: {
              offset: 1,
              span: 7,
            },
          },
          {
            id: "middleName",
            name: "middleName",
            inputType: "string",
            rule: "string",
            placeholder: "Middle Name",
            initialValue: opportunity.customer.middleName,
            label: "Middle Name",
            required: false,
            colConfig: {
              offset: 1,
              span: 7,
            },
          },
          {
            id: "lastName",
            name: "lastName",
            inputType: "string",
            rule: "string",
            placeholder: "Last Name",
            initialValue: opportunity.customer.lastName,
            label: "Last Name",
            required: true,
            colConfig: {
              span: 7,
            },
          },
          {
            id: "ssnitNumber",
            name: "ssnitNumber",
            inputType: "string",
            rule: "string",
            initialValue: opportunity.customer.ssnitNumber,
            required: false,
            disabled: false,
            placeholder: "SSNIT Number",
            label: "SSNIT Number",
            colConfig: {
              offset: 1,
              span: 7,
            },
          },
          {
            id: "dateOfBirth",
            name: "dateOfBirth",
            inputType: "date",
            rule: "string",
            placeholder: "Date of birth",
            label: "Date of birth",
            initialValue: opportunity.customer.dateOfBirth || undefined,
            required: false,
            dateOptions: {
              limitTo18: true,
            },
            colConfig: {
              offset: 1,
              span: 7,
            },
          },
          {
            id: "maritalStatusId",
            name: "maritalStatusId",
            inputType: "select",
            rule: "string",
            required: false,
            placeholder: "Marital Status",
            label: "Marital Status",
            selectOptions: maritalStatuses,
            initialValue: opportunity.customer.maritalStatus
              ? opportunity.customer.maritalStatus.id
              : undefined,
            colConfig: {
              span: 7,
            },
          },
          {
            id: "countryId",
            name: "countryId",
            inputType: "select",
            rule: "string",
            required: true,
            placeholder: "Nationality",
            label: "Nationality",
            selectOptions: countries,
            initialValue: opportunity.customer.country
              ? opportunity.customer.country.id
              : undefined,
            colConfig: {
              offset: 1,
              span: 7,
            },
          },
          {
            id: "gender",
            name: "gender",
            inputType: "select",
            rule: "string",
            placeholder: "Gender",
            label: "Gender",
            initialValue: opportunity.customer.gender
              ? opportunity.customer.gender
              : undefined,
            selectOptions: [
              { id: "0", name: "Male", value: "male" },
              { id: "1", name: "Female", value: "female" },
            ],
            required: true,
            colConfig: {
              offset: 1,
              span: 7,
            },
          },
          {
            id: "photoIdNumber",
            name: "photoIdNumber",
            inputType: "string",
            rule: "string",
            initialValue: opportunity.customer.photoIdNumber,
            required: false,
            disabled: false,
            placeholder: "Enter ID Number",
            label: "ID Number",
            colConfig: {
              span: 7,
            },
          },
          {
            id: "photoIdUrl",
            name: "photoIdUrl",
            inputType: "fileUpload",
            rule: "string",
            initialValue: opportunity.customer.photoIdUrl,
            required: false,
            disabled: false,
            label: "ID Upload",
            changeHandler: (combinedInfo: string) =>
              this.handleClientEditFileUploadChange("photoIdUrl", combinedInfo),
            defaultFileList: quickEditFileList,
            onFileDelete: this.handleClientEditDelete,
            colConfig: {
              offset: 1,
              span: 7,
            },
          },
          {
            id: "ghanaCardIdUrl",
            name: "ghanaCardIdUrl",
            inputType: "fileUpload",
            rule: "string",
            initialValue: opportunity.customer.ghanaCardIdUrl,
            required: false,
            disabled: false,
            label: "Ghana Card ID Upload",
            defaultFileList: quickEditGhanaCardFileList,
            changeHandler: (combinedInfo: string) =>
              this.handleClientEditFileUploadChange(
                "ghanaCardIdUrl",
                combinedInfo,
              ),
            onFileDelete: this.handleClientEditDelete,
            colConfig: {
              offset: 1,
              span: 7,
            },
          },
          {
            id: "ghanaCardIdBackUrl",
            name: "ghanaCardIdBackUrl",
            inputType: "fileUpload",
            rule: "string",
            initialValue: opportunity.customer.ghanaCardIdBackUrl,
            required: false,
            disabled: false,
            label: "Ghana Card ID Upload (Back)",
            defaultFileList: quickEditGhanaCardBackFileList,
            changeHandler: (combinedInfo: string) =>
              this.handleClientEditFileUploadChange(
                "ghanaCardIdBackUrl",
                combinedInfo,
              ),
            onFileDelete: this.handleClientEditDelete,
            colConfig: {
              span: 7,
            },
          },
          {
            id: "ghanaCardNo",
            name: "ghanaCardNo",
            inputType: "string",
            rule: "string",
            initialValue: opportunity.customer.ghanaCardNo,
            required: false,
            disabled: false,
            placeholder: "Enter Ghana Card ID Number",
            label: "Ghana Card ID Number",
            pattern: ghanaCardRegex,
            patternErrorMessage:
              "Invalid Ghana Card Number format. Expected format: ABC-123456789-1",
            colConfig: {
              offset: 1,
              span: 7,
            },
          },
          {
            id: "idIssueDate",
            name: "idIssueDate",
            inputType: "date",
            rule: "string",
            placeholder: "Ghana Card Issue Date",
            label: "Ghana Card Issue Date",
            initialValue: opportunity.customer.idIssueDate,
            required: false,
            elementStyle: {
              width: "100%",
            },
            dateOptions: {
              limitTo18: false,
              startYear: 2017,
            },
            colConfig: {
              offset: 1,
              span: 7,
            },
          },
          {
            id: "idExpiryDate",
            name: "idExpiryDate",
            inputType: "date",
            rule: "string",
            placeholder: "Ghana Card Expiry Date",
            label: "Ghana Card Expiry Date",
            initialValue: opportunity.customer.idExpiryDate,
            required: false,
            elementStyle: {
              width: "100%",
            },
            dateOptions: {
              limitTo18: false,
              startYear: 2017,
            },
            colConfig: {
              span: 7,
            },
          },
          {
            id: "signatureUrl",
            name: "signatureUrl",
            inputType: "fileUpload",
            rule: "string",
            initialValue: opportunity.customer.signatureUrl,
            required: false,
            disabled: false,
            label: "Signature Upload",
            defaultFileList: quickEditSignatureUpload,
            changeHandler: (combinedInfo: string) =>
              this.handleClientEditFileUploadChange(
                "signatureUrl",
                combinedInfo,
              ),
            onFileDelete: this.handleClientEditDelete,
            colConfig: {
              offset: 1,
              span: 7,
            },
          },
          {
            id: "isVerified",
            name: "isVerified",
            inputType: "checkbox",
            rule: "boolean",
            label: "Ghana Card & Date of Birth Verified?",
            checkboxCheckedValue:
              isClientEditVerified || !!opportunity.customer.verificationProofUrl,
            required: false,
            checkboxChangeHandler: (e) =>
              this.changeClientEditIsVerified(e.target.checked),
            colConfig: {
              span: 7,
              offset: 1,
            },
          },
          {
            id: "verificationProofUrl",
            name: "verificationProofUrl",
            inputType: "fileUpload",
            rule: "string",
            initialValue: opportunity.customer.verificationProofUrl,
            required: isClientEditVerified || false,
            disabled: false,
            label: "Proof of Verification",
            changeHandler: (combinedInfo: string) =>
              this.handleClientEditFileUploadChange(
                "verificationProofUrl",
                combinedInfo,
              ),
            onFileDelete: this.handleClientEditDelete,
            defaultFileList: opportunity.customer.verificationProofUrl
              ? [
                  {
                    size: 1000,
                    type: "Verification Proof",
                    uid: "1",
                    url: opportunity.customer.verificationProofUrl,
                    name: "Verification Proof",
                    thumbUrl: opportunity.customer.verificationProofUrl,
                  },
                ]
              : [],
            colConfig: {
              span: 7,
            },
          },
        ]
      : [];

    const companyClientEditFields: FormFieldConfig[] = opportunity?.company
      ? [
          {
            id: "name",
            name: "name",
            inputType: "string",
            rule: "string",
            required: true,
            placeholder: "Company Name",
            label: "Company Name",
            initialValue: opportunity.company.name,
            colConfig: { span: 7 },
          },
          {
            id: "tinNumber",
            name: "tinNumber",
            inputType: "string",
            rule: "string",
            required: false,
            placeholder: "TIN",
            label: "TIN",
            initialValue: opportunity.company.tinNumber,
            colConfig: { offset: 1, span: 7 },
          },
          {
            id: "industrySectorId",
            name: "industrySectorId",
            inputType: "select",
            rule: "string",
            required: false,
            placeholder: "Industry Sector",
            label: "Industry Sector",
            selectOptions: industrySectors,
            initialValue: opportunity.company.industrySector?.id,
            colConfig: { offset: 1, span: 7 },
          },
          {
            id: "numberOfEmployees",
            name: "numberOfEmployees",
            inputType: "number",
            rule: "number",
            required: false,
            placeholder: "Number of Employees",
            label: "No. of Employees",
            initialValue: opportunity.company.numberOfEmployees ?? undefined,
            colConfig: { span: 7 },
          },
          {
            id: "trusteeId",
            name: "trusteeId",
            inputType: "select",
            rule: "string",
            required: false,
            placeholder: "Trustee",
            label: "Trustee",
            selectOptions: trustees,
            initialValue: opportunity.company.trustee?.id,
            colConfig: { offset: 1, span: 7 },
          },
          {
            id: "relationshipManagerId",
            name: "relationshipManagerId",
            inputType: "select",
            rule: "string",
            required: false,
            placeholder: "Relationship Manager",
            label: "Relationship Manager",
            selectOptions: relationshipManagers,
            initialValue: opportunity.company.relationshipManager?.id,
            colConfig: { offset: 1, span: 7 },
          },
          {
            id: "ssnitNumber",
            name: "ssnitNumber",
            inputType: "string",
            rule: "string",
            required: false,
            placeholder: "SSNIT Number",
            label: "SSNIT Number",
            initialValue: opportunity.company.ssnitNumber || undefined,
            colConfig: { span: 7 },
          },
          {
            id: "rocNumber",
            name: "rocNumber",
            inputType: "string",
            rule: "string",
            required: false,
            placeholder: "RDG/ROC Number",
            label: "RDG/ROC Number",
            initialValue: opportunity.company.rocNumber || undefined,
            colConfig: { offset: 1, span: 7 },
          },
          {
            id: "dateOfEstablishment",
            name: "dateOfEstablishment",
            inputType: "date",
            rule: "string",
            required: false,
            placeholder: "Select date",
            label: "Date Of Establishment",
            initialValue: opportunity.company.dateOfEstablishment
              ? moment(opportunity.company.dateOfEstablishment)
              : undefined,
            colConfig: { offset: 1, span: 7 },
          },
          {
            id: "companySegment",
            name: "companySegment",
            inputType: "select",
            rule: "string",
            required: false,
            placeholder: "Company Segment",
            label: "Company Segment",
            initialValue: opportunity.company.companySegment || undefined,
            selectOptions: generateCompanySegments(),
            colConfig: { span: 7 },
          },
        ]
      : [];

    return (
      <Spin spinning={isLoading}>
        {isEditingProductData && opportunity ? (
          <EditOpportunity
            editingProduct={isEditingProductData}
            editingClientInfo={false}
            updateCompanyPaymentInformation={
              this.updateCompanyPaymentInformation
            }
            updateCustomerPaymentInformation={
              this.updateCustomerPaymentInformation
            }
            opportunity={opportunity}
            customer={opportunity.customer as Customer}
            company={opportunity.company as Company}
            countries={countries}
            updateData={this.saveData}
            updateBeneficiaryInformation={this.updateBeneficiaryInformation}
            loadOpportunity={this.getOpportunity}
            cancelEdit={this.cancelEdit}
            isEditing={true}
          />
        ) : (
          <Card>
            <Fragment>
              {opportunity && (
                <div>
                  <EntitledSection
                    entitlement={entitlements.BusinessDevelopment.moveToWon}
                  >
                    {(() => {
                      const usersPlusSalesPerson = getUniqueArrayObjects(
                        opportunity?.salesPerson
                          ? [
                              ...salesPersons,
                              {
                                id: opportunity.salesPerson.id,
                                name: opportunity.salesPerson.name,
                              },
                            ]
                          : salesPersons,
                      );
                      const visibleProductEditFieldsToHide =
                        productEditFieldsToHide.filter(
                          (field) =>
                            field !== "monthlyExpectedContribution" &&
                            field !== "lumpsumExpectedContribution",
                        );
                      const monthlyContributionDisabled =
                        productEditFieldsToHide.includes(
                          "monthlyExpectedContribution",
                        );
                      const lumpSumContributionDisabled =
                        productEditFieldsToHide.includes(
                          "lumpsumExpectedContribution",
                        );
                      const productEditFields: FormFieldConfig[] = [
                        {
                          id: "product",
                          name: "product",
                          inputType: "select",
                          rule: "string",
                          label: "Product",
                          placeholder: "Select Product",
                          required: true,
                          initialValue: opportunity.product?.id,
                          selectOptions: remodelProducts(petraProducts),
                          changeHandler: this.showCategoryAndTierForModal,
                          colConfig: { span: 7 },
                        },
                        {
                          id: "pCategory",
                          name: "pCategory",
                          inputType: "description",
                          disabled: true,
                          label: "Product Category and Tier",
                          placeholder: "No Product Selected",
                          required: false,
                          value: productCategoryAndTier,
                          colConfig: { offset: 1, span: 7 },
                        },
                        {
                          id: "productPaymentType",
                          name: "productPaymentType",
                          inputType: "select",
                          rule: "string",
                          label: "Product Payment Type",
                          placeholder: "Select Payment Type",
                          required: false,
                          initialValue: opportunity.productPaymentType,
                          selectOptions: Object.entries(
                            productPaymentTypes,
                          ).map(([k, v]) => ({
                            id: k,
                            name: v as string,
                            value: k,
                          })),
                          colConfig: { offset: 1, span: 7 },
                        },
                        {
                          id: "contributionType",
                          name: "contributionType",
                          inputType: "select",
                          rule: "string",
                          placeholder: "Contribution Type",
                          label: "Contribution Type",
                          initialValue: opportunity.contributionType,
                          selectOptions: generateContributionTypes(),
                          changeHandler: this.showContributionFieldsForModal,
                          required: false,
                          colConfig: { span: 7 },
                        },
                        {
                          id: "monthlyExpectedContribution",
                          name: "monthlyExpectedContribution",
                          inputType: "number",
                          rule: "number",
                          label: "Expected Monthly Contribution",
                          placeholder: "Monthly Contribution",
                          required: false,
                          disabled: monthlyContributionDisabled,
                          initialValue:
                            opportunity.monthlyExpectedContribution ??
                            undefined,
                          elementStyle: { width: "100%" },
                          colConfig: { offset: 1, span: 7 },
                        },
                        {
                          id: "lumpsumExpectedContribution",
                          name: "lumpsumExpectedContribution",
                          inputType: "number",
                          rule: "number",
                          label: "Expected Lump Sum Contribution",
                          placeholder: "Lump Sum Contribution",
                          required: false,
                          disabled: lumpSumContributionDisabled,
                          initialValue:
                            opportunity.lumpsumExpectedContribution ??
                            undefined,
                          elementStyle: { width: "100%" },
                          colConfig: { offset: 1, span: 7 },
                        },
                        {
                          id: "signupReason",
                          name: "signupReason",
                          inputType: "select",
                          rule: "string",
                          label: "Why did you choose this product?",
                          initialValue: opportunity.signupReason,
                          selectOptions: [
                            {
                              id: "1",
                              name: "I trust Petra",
                              value: "I trust Petra",
                            },
                            {
                              id: "2",
                              name: "I am diversifying my investment",
                              value: "I am diversifying my investment",
                            },
                            {
                              id: "3",
                              name: "I am trying the product",
                              value: "I am trying the product",
                            },
                            {
                              id: "4",
                              name: "The return attracted me",
                              value: "The return attracted me",
                            },
                            {
                              id: "5",
                              name: "A friend recommended",
                              value: "A friend recommended",
                            },
                            {
                              id: "6",
                              name: "The product's features",
                              value: "The product's features",
                            },
                            {
                              id: "7",
                              name: "Required by law for institutions to pay for employees",
                              value:
                                "Required by law for institutions to pay for employees",
                            },
                          ],
                          placeholder:
                            "Enter a reason for choosing this product...",
                          required: false,
                          colConfig: { span: 7 },
                        },
                        {
                          id: "sourceOfAwareness",
                          name: "sourceOfAwareness",
                          inputType: "select",
                          rule: "string",
                          label: "How did you hear about Petra?",
                          initialValue: opportunity.sourceOfAwareness,
                          placeholder: "Choose source of awareness",
                          required: true,
                          selectOptions: [
                            {
                              id: "1",
                              name: "Social Media",
                              value: "Social Media",
                            },
                            { id: "2", name: "Family", value: "Family" },
                            {
                              id: "3",
                              name: "Billboards",
                              value: "Billboards",
                            },
                            { id: "4", name: "Radio", value: "Radio" },
                            {
                              id: "5",
                              name: "Newspapers/Magazines",
                              value: "Newspapers/Magazines",
                            },
                            {
                              id: "6",
                              name: "Word of mouth/Referrals",
                              value: "Word of mouth/Referrals",
                            },
                            {
                              id: "7",
                              name: "Petra Representative",
                              value: "Petra Representative",
                            },
                            {
                              id: "8",
                              name: "Email From Petra Trust",
                              value: "Email From Petra Trust",
                            },
                            { id: "9", name: "Website", value: "Website" },
                          ],
                          colConfig: { offset: 1, span: 7 },
                        },
                        {
                          id: "salesPersonId",
                          name: "salesPersonId",
                          placeholder: "Enter sales person's name ...",
                          required: false,
                          inputType: "search",
                          label: "Sales Person",
                          changeHandler:
                            this.handleSalesPersonSearchInputForModal,
                          initialValue: opportunity.salesPerson?.id,
                          selectOptions: usersPlusSalesPerson,
                          colConfig: { offset: 1, span: 7 },
                        },
                        {
                          id: "userId",
                          name: "userId",
                          placeholder: "Enter user's name ...",
                          required: false,
                          inputType: "search",
                          label: "Assigned To",
                          changeHandler: this.handleUserSearchInput,
                          initialValue: opportunity.assignee?.id,
                          selectOptions: usersPlusAssignee,
                          colConfig: { span: 7 },
                        },
                        ...(!opportunity.customer
                          ? [
                              {
                                id: "contributionAllocationTypes",
                                name: "contributionAllocationTypes",
                                inputType: "multipleSelect",
                                rule: "array",
                                label: "Contribution Allocation Types",
                                placeholder:
                                  "Select contribution allocation types...",
                                required: false,
                                initialValue:
                                  opportunity.contributionAllocationTypes ?? [],
                                selectOptions:
                                  getContributionAllocationTypeOptions(
                                    opportunity.product?.name,
                                  ),
                                colConfig: { offset: 1, span: 7 },
                              } as FormFieldConfig,
                            ]
                          : []),
                      ];

                      return ModalCreator({
                        title: "Edit Opportunity",
                        showModal: showProductEditModal,
                        okType: "primary",
                        okText: "Save",
                        onOkay: this.saveProductEdit,
                        isLoading: isProductEditSaving,
                        toggleModal: () =>
                          this.setState({ showProductEditModal: false }),
                        width: 1000,
                        inscription: (
                          <BaseForm
                            key="product-edit-modal"
                            layout="vertical"
                            reference={this.productEditReference}
                            fieldsToHide={visibleProductEditFieldsToHide}
                            fields={productEditFields}
                          />
                        ),
                      });
                    })()}
                    {ModalCreator({
                      isLoading,
                      title: "Onboarding Checklist",
                      showModal: this.state.showChecklist,
                      onOkay: this.winOpportunity,
                      toggleModal: this.toggleModal,
                      okText: "Move to won",
                      okayButtonProps: {
                        disabled: checklistData.percentage !== 100,
                      },
                      inscription: (
                        <CheckList
                          opportunity={opportunity}
                          percentage={checklistData.percentage}
                          results={checklistData.result}
                        />
                      ),
                    })}
                    {ModalCreator({
                      title: opportunity.customer
                        ? "Edit Customer"
                        : "Edit Company Details",
                      showModal: isEditingClientData,
                      okType: "primary",
                      okText: "Save",
                      onOkay: this.saveClientProfileEdit,
                      isLoading: isClientEditSaving,
                      toggleModal: this.cancelEdit,
                      width: 1000,
                      inscription: (
                        <BaseForm
                          key={
                            opportunity.customer
                              ? "opportunity-customer-edit"
                              : "opportunity-company-edit"
                          }
                          layout="vertical"
                          reference={this.clientEditReference}
                          fieldsToHide={[]}
                          fields={
                            opportunity.customer
                              ? customerClientEditFields
                              : companyClientEditFields
                          }
                        />
                      ),
                    })}
                    {showDialog &&
                      ModalCreator({
                        title: "Confirm Deletion Action",
                        okText: "Yes",
                        okType: "danger",
                        showModal: showDialog,
                        onOkay: this.deleteOpportunity,
                        toggleModal: this.toggleDelete,
                        inscription: (
                          <div>
                            Are you sure you want to delete this Opportunity?
                          </div>
                        ),
                      })}
                    {ModalCreator({
                      title:
                        this.state.contactModalType &&
                        this.state.contactModalMode
                          ? `${
                              this.state.contactModalMode === "edit"
                                ? "Edit"
                                : "Add"
                            } ${
                              this.state.contactModalType === "email"
                                ? "Email"
                                : this.state.contactModalType === "phone"
                                ? "Phone"
                                : "Address"
                            }`
                          : "Contact",
                      okType: "primary",
                      okText:
                        this.state.contactModalMode === "edit"
                          ? "Update"
                          : "Save",
                      showModal: this.state.showContactModal,
                      onOkay:
                        this.state.contactModalType === "email"
                          ? this.saveContactEmail
                          : this.state.contactModalType === "phone"
                          ? this.saveContactPhone
                          : this.saveContactAddress,
                      toggleModal: this.closeContactModal,
                      isLoading: this.state.contactModalLoading,
                      width:
                        this.state.contactModalType === "address" ? 800 : 560,
                      inscription: (
                        <BaseForm
                          key={`${this.state.contactModalType}-${
                            this.state.contactModalMode
                          }-${
                            this.state.contactModalType === "email"
                              ? this.state.selectedEmail?.id ?? "new"
                              : this.state.contactModalType === "phone"
                              ? this.state.selectedPhone?.id ?? "new"
                              : this.state.selectedAddress?.id ?? "new"
                          }`}
                          layout="vertical"
                          reference={
                            this.state.contactModalType === "email"
                              ? this.contactEmailReference
                              : this.state.contactModalType === "phone"
                              ? this.contactPhoneReference
                              : this.contactAddressReference
                          }
                          fieldsToHide={[]}
                          fields={
                            this.state.contactModalType === "email"
                              ? createEmailFields({
                                  createEmail: this.saveContactEmail,
                                  initialValue:
                                    this.state.contactModalMode === "edit"
                                      ? this.state.selectedEmail || undefined
                                      : undefined,
                                }).filter(
                                  (field) =>
                                    field.inputType !== "button" &&
                                    field.inputType !== "buttonGroup",
                                )
                              : this.state.contactModalType === "phone"
                              ? createPhoneFields({
                                  createPhone: this.saveContactPhone,
                                  initialValue:
                                    this.state.contactModalMode === "edit"
                                      ? this.state.selectedPhone || undefined
                                      : undefined,
                                }).filter(
                                  (field) =>
                                    field.inputType !== "button" &&
                                    field.inputType !== "buttonGroup",
                                )
                              : createAddressFields({
                                  countries: this.state.countries,
                                  createAddress: this.saveContactAddress,
                                  initialValue:
                                    this.state.contactModalMode === "edit"
                                      ? this.state.selectedAddress || undefined
                                      : undefined,
                                  changeHandler: this.handleAddressTypeChange,
                                  addressType: this.state.addressType,
                                }).filter(
                                  (field) =>
                                    field.inputType !== "button" &&
                                    field.inputType !== "buttonGroup",
                                )
                          }
                        />
                      ),
                    })}
                    {ModalCreator({
                      title:
                        backgroundInfoMode === "edit"
                          ? "Edit Background Info"
                          : "Add Background Info",
                      okType: "primary",
                      okText: backgroundInfoMode === "edit" ? "Update" : "Save",
                      showModal: showBackgroundInfoModal,
                      onOkay: this.saveBackgroundInfo,
                      toggleModal: this.closeBackgroundInfoModal,
                      isLoading: isBackgroundInfoSaving,
                      width: 700,
                      inscription: (
                        <BaseForm
                          layout="vertical"
                          reference={this.backgroundInfoReference}
                          fieldsToHide={[]}
                          fields={backgroundInfoFields}
                        />
                      ),
                    })}
                    {ModalCreator({
                      title: "Delete Background Info",
                      okType: "primary",
                      okText: "Delete",
                      showModal: showBackgroundInfoDeleteModal,
                      onOkay: this.deleteBackgroundInfo,
                      toggleModal: this.closeBackgroundInfoDeleteModal,
                      isLoading: isBackgroundInfoSaving,
                      inscription:
                        "Are you sure you want to delete background information?",
                    })}
                    {ModalCreator({
                      title:
                        this.state.deleteContactType === "email"
                          ? "Delete Email"
                          : this.state.deleteContactType === "phone"
                            ? "Delete Phone"
                            : "Delete Address",
                      okType: "primary",
                      okText: "Delete",
                      showModal: this.state.showContactDeleteModal,
                      onOkay: this.confirmDeleteContact,
                      toggleModal: this.closeDeleteContactModal,
                      isLoading: this.state.contactModalLoading,
                      inscription:
                        this.state.deleteContactType === "email"
                          ? "Are you sure you want to delete this email?"
                          : this.state.deleteContactType === "phone"
                            ? "Are you sure you want to delete this phone?"
                            : "Are you sure you want to delete this address?",
                    })}
                    {ModalCreator({
                      title: "Customer Associations",
                      showModal: this.state.showAssociationsModal,
                      width: 900,
                      toggleModal: this.closeAssociationsModal,
                      showFooter: false,
                      inscription: opportunity?.customer ? (
                        <CustomerOnboardingDetails
                  ref={this.associationsModalRef}
                  isEditing={true}
                  opportunity={opportunity}
                  customer={opportunity.customer}
                  loadOpportunity={this.getOpportunity}
                  financialInstitutions={this.state.financialInstitutions}
                  branchMap={this.state.branchMap}
                  companies={this.state.companies}
                  subscriptions={opportunity.customerSubscriptions || []}
                  customerAssociations={opportunity.customer.customerCompanyAssociations || []}
                  showBeneficiarySection={false}
                  showCustomerAssociationSection={true}
                  showOnboardingDetailsSection={false}
                  visibleViews={[CustomerOnboardingViews.customerAssociation]}
                  showList={false}
                  renderInline={true}
                  hideFormOnDelete={true}
                  onCancelEdit={this.closeAssociationsModal}
                  activekey={CustomerOnboardingViews.customerAssociation}
                  updateBeneficiaryInformation={this.updateBeneficiaryInformation}
                  updateCustomerPaymentInformation={
                    this.updateCustomerPaymentInformation
                  }
                  updateCustomerCompanyAssociationsInformation={
                    this.updateCustomerCompanyAssociationsInformation
                  }
                />
                      ) : (
                        <div />
                      ),
                    })}
                    {ModalCreator({
                      title: "Customer Beneficiaries",
                      showModal: this.state.showBeneficiariesModal,
                      toggleModal: this.closeBeneficiariesModal,
                      showFooter: false,
                      width: 900,
                      inscription: opportunity?.customer ? (
                        <CustomerOnboardingDetails
                          ref={this.beneficiariesModalRef}
                          isEditing={true}
                          opportunity={opportunity}
                          customer={opportunity.customer}
                          loadOpportunity={this.getOpportunity}
                          financialInstitutions={this.state.financialInstitutions}
                          branchMap={this.state.branchMap}
                          companies={this.state.companies}
                          subscriptions={opportunity.customerSubscriptions || []}
                          customerAssociations={opportunity.customer.customerCompanyAssociations || []}
                          showBeneficiarySection={true}
                          showCustomerAssociationSection={false}
                          showOnboardingDetailsSection={false}
                          visibleViews={[CustomerOnboardingViews.beneficiary]}
                          showList={false}
                          renderInline={true}
                          hideFormOnDelete={true}
                          onCancelEdit={this.closeBeneficiariesModal}
                          activekey={this.state.beneficiariesActiveKey}
                          updateBeneficiaryInformation={this.updateBeneficiaryInformation}
                          updateCustomerPaymentInformation={this.updateCustomerPaymentInformation}
                          updateCustomerCompanyAssociationsInformation={
                            this.updateCustomerCompanyAssociationsInformation
                          }
                        />
                      ) : (
                        <div />
                      ),
                    })}
                    {ModalCreator({
                      title: "Payment Accounts",
                      showModal: this.state.showPaymentAccountsModal,
                      toggleModal: this.closePaymentAccountsModal,
                      showFooter: false,
                      width: 900,
                      inscription: opportunity?.customer ? (
                        <CustomerOnboardingDetails
                          ref={this.paymentAccountsModalRef}
                          isEditing={true}
                          opportunity={opportunity}
                          customer={opportunity.customer}
                          loadOpportunity={this.getOpportunity}
                          financialInstitutions={this.state.financialInstitutions}
                          branchMap={this.state.branchMap}
                          companies={this.state.companies}
                          subscriptions={opportunity.customerSubscriptions || []}
                          customerAssociations={opportunity.customer.customerCompanyAssociations || []}
                          showBeneficiarySection={false}
                          showCustomerAssociationSection={false}
                          showOnboardingDetailsSection={false}
                          visibleViews={
                            this.state.paymentAccountsActiveKey ===
                            CustomerOnboardingViews.debit
                              ? [CustomerOnboardingViews.debit]
                              : [CustomerOnboardingViews.payout]
                          }
                          showList={false}
                          renderInline={true}
                          hideFormOnDelete={true}
                          onCancelEdit={this.closePaymentAccountsModal}
                          activekey={this.state.paymentAccountsActiveKey}
                          updateBeneficiaryInformation={this.updateBeneficiaryInformation}
                          updateCustomerPaymentInformation={this.updateCustomerPaymentInformation}
                          updateCustomerCompanyAssociationsInformation={
                            this.updateCustomerCompanyAssociationsInformation
                          }
                        />
                      ) : opportunity?.company ? (
                        <CompanyOnboardingDetails
                          ref={this.companyPaymentModalRef}
                          isEditing={true}
                          opportunity={
                            { company: opportunity.company } as Opportunity
                          }
                          company={opportunity.company}
                          loadOpportunity={this.getOpportunity}
                          financialInstitutions={
                            this.state.financialInstitutions
                          }
                          branchMap={this.state.branchMap}
                          updateCompanyPaymentInformation={
                            this.updateCompanyPaymentInformation
                          }
                          visibleViews={[CompanyOnboardingViews.payout]}
                          showList={false}
                          renderInline={true}
                          hideFormOnDelete={true}
                          showInlineHeader={false}
                          activekey={CompanyOnboardingViews.payout}
                        />
                      ) : (
                        <div />
                      ),
                    })}
                    {ModalCreator({
                      title: this.state.currentCompanyContact
                        ? "Edit Company Contact"
                        : "Add Company Contact",
                      okType: "primary",
                      okText: this.state.currentCompanyContact
                        ? "Update"
                        : "Save",
                      showModal: this.state.showCompanyContactModal,
                      onOkay: this.saveCompanyContactPerson,
                      toggleModal: this.closeCompanyContactModal,
                      isLoading: this.state.contactModalLoading,
                      width: 700,
                      inscription: (
                        <BaseForm
                          key={`company-contact-form-${
                            this.state.currentCompanyContact?.id ?? "new"
                          }`}
                          layout="vertical"
                          reference={this.companyContactPersonFormReference}
                          fields={companyContactPersonFormFields}
                          fieldsToHide={companyContactFieldsToHide}
                        />
                      ),
                    })}
                    {ModalCreator({
                      title: "Add Company Director",
                      okType: "primary",
                      okText: "Save",
                      showModal: this.state.showCompanyDirectorModal,
                      onOkay: this.createCompanyDirector,
                      toggleModal: () => this.toggleCompanyDirectorModal(false),
                      isLoading: this.state.isLoading,
                      width: 600,
                      inscription: (
                        <BaseForm
                          layout="vertical"
                          reference={this.newCompanyDirectorFormReference}
                          fieldsToHide={[]}
                          fields={[
                            {
                              id: "firstName",
                              name: "firstName",
                              inputType: "string",
                              rule: "string",
                              placeholder: "First Name",
                              label: "First Name",
                              required: true,
                              colConfig: { span: 11 },
                            },
                            {
                              id: "lastName",
                              name: "lastName",
                              inputType: "string",
                              rule: "string",
                              placeholder: "Last Name",
                              label: "Last Name",
                              required: true,
                              colConfig: { span: 11, offset: 1 },
                            },
                          ]}
                        />
                      ),
                    })}
                    {ModalCreator({
                      title: "Add Director Contact",
                      okType: "primary",
                      okText: "Add Contact",
                      showModal: this.state.showCompanyDirectorContactModal,
                      onOkay: this.createCompanyDirectorContact,
                      toggleModal: this.closeCompanyDirectorContactModal,
                      isLoading: this.state.isLoading,
                      width: 600,
                      inscription: (
                        <BaseForm
                          layout="vertical"
                          reference={this.newCompanyDirectorContactFormReference}
                          fieldsToHide={[]}
                          fields={[
                            {
                              id: "phoneNumber",
                              name: "phoneNumber",
                              inputType: "string",
                              rule: "string",
                              placeholder: "Phone Number",
                              label: "Phone Number",
                              required: false,
                              colConfig: { span: 11 },
                            },
                            {
                              id: "emailAddress",
                              name: "emailAddress",
                              inputType: "string",
                              rule: "string",
                              placeholder: "Email Address",
                              label: "Email Address",
                              required: false,
                              colConfig: { span: 11, offset: 1 },
                            },
                          ]}
                        />
                      ),
                    })}
                    {this.state.showAddDocumentModal &&
                      ModalCreator({
                        title: "Add Required Document",
                        okType: "primary",
                        okText: "Close",
                        showModal: this.state.showAddDocumentModal,
                        showFooter: true,
                        onOkay: () => this.toggleFileModal(false),
                        toggleModal: () => this.toggleFileModal(false),
                        width: 600,
                        inscription: this.state.opportunity?.company ? (
                          (() => {
                            const opportunity = this.state
                              .opportunity as Opportunity;
                            const documentUploadsFields =
                              createDocumentUploadFormFields({
                                opportunity,
                                company: this.state.opportunity?.company,
                                handleFileUploadSuccess:
                                  this.handleDocumentUploadSuccess,
                                initialValues: {
                                  npraForm: opportunity.npraForm,
                                  vestingRuleDocument:
                                    opportunity.vestingRuleDocument,
                                  employeeDatasheet:
                                    opportunity.employeeDatasheet,
                                  signedApplicationForm:
                                    opportunity.signedApplicationForm,
                                },
                              });
                            return (
                              <BaseForm
                                reference={this.docsReference}
                                fields={documentUploadsFields}
                                layout="vertical"
                                fieldsToHide={[]}
                              />
                            );
                          })()
                        ) : this.state.opportunity?.customer ? (
                          (() => {
                            const opportunity = this.state
                              .opportunity as Opportunity;
                            const customer = this.state.opportunity?.customer;
                            const signedApplicationUpload =
                              customer?.customerInfoFileUploads?.find(
                                (file) =>
                                  file.fileType ===
                                  FileTypes.signedApplicationForm,
                              ) || opportunity.signedApplicationForm;
                            const directDebitUpload =
                              customer?.customerInfoFileUploads?.find(
                                (file) =>
                                  file.fileType ===
                                  FileTypes.directDebitAuthorizationForm,
                              ) || opportunity.directDebitAuthorizationForm;

                            let allFields =
                              createCustomerDocumentUploadFormFields({
                                opportunity,
                                customer,
                                handleFileUploadSuccess:
                                  this.handleDocumentUploadSuccess,
                                initialValues: {
                                  signedApplicationForm:
                                    signedApplicationUpload,
                                  directDebitAuthorizationForm:
                                    directDebitUpload,
                                  customerInfoFileUploads:
                                    customer?.customerInfoFileUploads,
                                },
                              });

                            allFields = allFields.map((field) => {
                              let fileTypeToCheck = "";
                              if (field.name === "signedApplicationFormUrl") {
                                fileTypeToCheck =
                                  FileTypes.signedApplicationForm;
                              } else if (
                                field.name === "directDebitAuthorizationForm"
                              ) {
                                fileTypeToCheck =
                                  FileTypes.directDebitAuthorizationForm;
                              }
                              const existsInCustomerUploads =
                                !!fileTypeToCheck &&
                                !!customer?.customerInfoFileUploads?.some(
                                  (f) => f.fileType === fileTypeToCheck,
                                );
                              const existsInRequiredDocs =
                                (fileTypeToCheck ===
                                  FileTypes.signedApplicationForm &&
                                  !!opportunity.signedApplicationForm?.url) ||
                                (fileTypeToCheck ===
                                  FileTypes.directDebitAuthorizationForm &&
                                  !!opportunity.directDebitAuthorizationForm
                                    ?.url);

                              if (
                                existsInCustomerUploads ||
                                existsInRequiredDocs
                              ) {
                                return { ...field, initialValue: "exists" };
                              }
                              return field;
                            });

                            const documentModalFields = allFields.filter(
                              (field) => !field.initialValue,
                            );
                            return documentModalFields.length === 0 ? (
                              <Empty description="All required documents are already on file." />
                            ) : (
                              <BaseForm
                                reference={this.docsReference}
                                fields={documentModalFields}
                                layout="vertical"
                                fieldsToHide={[]}
                              />
                            );
                          })()
                        ) : (
                          <div />
                        ),
                      })}
                    {ModalCreator({
                      title: "Delete Document",
                      okType: "primary",
                      okText: "Delete",
                      showModal: this.state.showDeleteConfirmationModal,
                      onOkay: this.confirmDeleteDocument,
                      toggleModal: this.toggleDeleteConfirmationModal,
                      isLoading: this.state.isLoading,
                      inscription:
                        "Are you sure you want to delete this document?",
                    })}
                    {ModalCreator({
                      title:
                        this.state.deleteTargetType === "association"
                          ? "Delete Association"
                          : this.state.deleteTargetType === "companyContact"
                          ? "Delete Company Contact"
                          : this.state.deleteTargetType === "beneficiary"
                          ? "Delete Beneficiary"
                          : this.state.deleteTargetType === "payout"
                          ? "Delete Payout Account"
                          : this.state.deleteTargetType === "debit"
                          ? "Delete Debit Account"
                          : this.state.deleteTargetType === "director"
                          ? "Delete Director"
                          : "Delete",
                      okType: "primary",
                      okText: "Delete",
                      showModal: this.state.showStandaloneDeleteModal,
                      onOkay: this.confirmStandaloneDelete,
                      toggleModal: this.closeStandaloneDeleteModal,
                      isLoading: this.state.contactModalLoading,
                      inscription: "Are you sure you want to delete this item?",
                    })}
                    <Row
                      gutter={[16, 16]}
                      className="view-opportunity-row"
                      justify="end"
                    >
                      <EntitledSection
                        entitlement={entitlements.BusinessDevelopment.changeLog}
                      >
                        <Col style={{ textAlign: "right" }}>
                          {showMoveToInprogressButton && (
                            <Button
                              onClick={this.moveToInProgress}
                              className={this.generateButtonColor(
                                opportunity.progressStatus,
                              )}
                            >
                              Move to In Progress
                            </Button>
                          )}

                          {showAssignPetraIdButton && (
                            <Button
                              onClick={this.assignPetraId}
                              className={this.generateButtonColor(
                                opportunity.progressStatus,
                              )}
                            >
                              Assign Petra Id
                            </Button>
                          )}

                          {showInitiateOnboardingButton && (
                            <Button
                              className={this.generateButtonColor(
                                opportunity.progressStatus,
                              )}
                              onClick={this.toggleModal}
                            >
                              Initiate Onboarding
                            </Button>
                          )}
                          <EntitledSection
                            entitlement={entitlements.Products.delete}
                          >
                            <AntdButton
                              style={{
                                marginLeft: "8px",
                              }}
                              danger
                              onClick={this.toggleDelete}
                            >
                              Delete Opportunity
                            </AntdButton>
                          </EntitledSection>
                          <Button
                            className="base-button"
                            id="toggle-audit-drawer-btn"
                            onClick={this.toggleDrawer("audits")}
                            style={{ marginLeft: "8px" }}
                          >
                            Audit Logs
                          </Button>
                          <Button
                            style={{ marginLeft: "8px" }}
                            className="base-button"
                            onClick={this.toggleDrawer("notifierRecords")}
                          >
                            Notifications
                          </Button>
                        </Col>
                      </EntitledSection>
                    </Row>
                  </EntitledSection>
                  <Row></Row>

                  <OpportunityDetails
                    toggleDelete={this.toggleDelete}
                    opportunity={opportunity}
                    editButton={productDataEditButton}
                  />

                  {opportunity.customer && (
                    <IndividualInformation
                      editButton={clientDetailsEditButton}
                      opportunity={opportunity}
                      onAddEmail={ifNotWon(() => this.openContactModal("email", "add"))}
                      onAddPhone={ifNotWon(() => this.openContactModal("phone", "add"))}
                      onAddAddress={ifNotWon(() => this.openContactModal("address", "add"))}
                      onEditEmail={ifNotWon((email: AssociatedEmail) => this.openContactModal("email", "edit", email))}
                      onEditPhone={ifNotWon((phone: AssociatedPhone) => this.openContactModal("phone", "edit", phone))}
                      onEditAddress={ifNotWon((address: AssociatedAddress) => this.openContactModal("address", "edit", address))}
                      onDeleteEmail={ifNotWon((email: AssociatedEmail) => this.openDeleteContactModal("email", email.id))}
                      onDeletePhone={ifNotWon((phone: AssociatedPhone) => this.openDeleteContactModal("phone", phone.id))}
                      onDeleteAddress={ifNotWon((address: AssociatedAddress) => this.openDeleteContactModal("address", address.id))}
                      onMakePrimaryEmail={ifNotWon(this.markEmailAsPrimary)}
                      onMakePrimaryPhone={ifNotWon(this.markPhoneAsPrimary)}
                      onMakePrimaryAddress={ifNotWon(this.markAddressAsPrimary)}
                      onReactivateEmail={ifNotWon(this.reactivateEmail)}
                      onReactivatePhone={ifNotWon(this.reactivatePhone)}
                      onReactivateAddress={ifNotWon(this.reactivateAddress)}
                      onAddAssociation={ifNotWon(this.openAssociationsModal)}
                      onEditAssociation={ifNotWon(this.openAssociationsModal)}
                      onDeleteAssociation={ifNotWon(this.openAssociationsDeleteModal)}
                      onMarkAssociationAsFormer={ifNotWon(
                        (association: CustomerCompanyAssociation) =>
                          void this.setAssociationEmploymentStatus(
                            association,
                            false,
                          ),
                      )}
                      onMarkAssociationAsCurrent={ifNotWon(
                        (association: CustomerCompanyAssociation) =>
                          void this.setAssociationEmploymentStatus(
                            association,
                            true,
                          ),
                      )}
                      onAddBeneficiary={ifNotWon(() => this.openBeneficiariesModal())}
                      onEditBeneficiary={ifNotWon(this.openBeneficiariesModal)}
                      onDeleteBeneficiary={ifNotWon(this.openBeneficiariesDeleteModal)}
                      onAddPayoutAccount={ifNotWon(() => this.openPaymentAccountsModal(undefined, "payout"))}
                      onAddDebitAccount={ifNotWon(() => this.openPaymentAccountsModal(undefined, "debit"))}
                      onEditPayoutAccount={ifNotWon((account: PaymentAccount) => this.openPaymentAccountsModal(account, "payout"))}
                      onDeletePayoutAccount={ifNotWon((account: PaymentAccount) => this.openPaymentAccountsDeleteModal(account, "payout"))}
                      onEditDebitAccount={ifNotWon((account: PaymentAccount) => this.openPaymentAccountsModal(account, "debit"))}
                      onDeleteDebitAccount={ifNotWon((account: PaymentAccount) => this.openPaymentAccountsDeleteModal(account, "debit"))}
                      onUploadCustomerInfoFile={ifNotWon(() => this.toggleFileModal(true))}
                      onDeleteCustomerInfoFile={ifNotWon(this.toggleDeleteConfirmationModal)}
                      onSetEntityToBeDeleted={ifNotWon(this.setEntityToBeDeleted)}
                      onAddBackgroundInfo={ifNotWon(() => this.openBackgroundInfoModal("add"))}
                      onEditBackgroundInfo={ifNotWon(() => this.openBackgroundInfoModal("edit"))}
                      onDeleteBackgroundInfo={ifNotWon(this.openBackgroundInfoDeleteModal)}
                    />
                  )}
                  {opportunity.company && (
                    <CompanyInformation
                      editButton={clientDetailsEditButton}
                      opportunity={opportunity}
                      onAddEmail={ifNotWon(() => this.openContactModal("email", "add"))}
                      onAddPhone={ifNotWon(() => this.openContactModal("phone", "add"))}
                      onAddAddress={ifNotWon(() => this.openContactModal("address", "add"))}
                      onEditEmail={ifNotWon((email: AssociatedEmail) => this.openContactModal("email", "edit", email))}
                      onEditPhone={ifNotWon((phone: AssociatedPhone) => this.openContactModal("phone", "edit", phone))}
                      onEditAddress={ifNotWon((address: AssociatedAddress) => this.openContactModal("address", "edit", address))}
                      onDeleteEmail={ifNotWon((email: AssociatedEmail) => this.openDeleteContactModal("email", email.id))}
                      onDeletePhone={ifNotWon((phone: AssociatedPhone) => this.openDeleteContactModal("phone", phone.id))}
                      onDeleteAddress={ifNotWon((address: AssociatedAddress) => this.openDeleteContactModal("address", address.id))}
                      onMakePrimaryEmail={ifNotWon(this.markEmailAsPrimary)}
                      onMakePrimaryPhone={ifNotWon(this.markPhoneAsPrimary)}
                      onMakePrimaryAddress={ifNotWon(this.markAddressAsPrimary)}
                      onReactivateEmail={ifNotWon(this.reactivateEmail)}
                      onReactivatePhone={ifNotWon(this.reactivatePhone)}
                      onReactivateAddress={ifNotWon(this.reactivateAddress)}
                      onAddCompanyContact={ifNotWon(this.openCompanyContactModal)}
                      onEditCompanyContact={ifNotWon(this.openCompanyContactModal)}
                      onDeleteCompanyContact={ifNotWon(this.openCompanyContactDeleteModal)}
                      onAddCompanyDirector={ifNotWon(() => this.toggleCompanyDirectorModal(true))}
                      onDeleteCompanyDirector={ifNotWon(this.openCompanyDirectorDeleteModal)}
                      onAddDirectorContact={ifNotWon(this.openCompanyDirectorContactModal)}
                      onDeleteDirectorContact={ifNotWon(this.deleteDirectorContact)}
                      onUploadCompanyInfoFile={ifNotWon(() => this.toggleFileModal(true))}
                      onDeleteCompanyInfoFile={ifNotWon(this.toggleDeleteConfirmationModal)}
                      onSetEntityToBeDeleted={ifNotWon(this.setEntityToBeDeleted)}
                      companyInfoFileUploads={opportunityRequiredDocuments}
                      onAddPayoutAccount={ifNotWon(() => this.openPaymentAccountsModal(undefined, "payout"))}
                      onAddDebitAccount={ifNotWon(() => this.openPaymentAccountsModal(undefined, "debit"))}
                      onEditPayoutAccount={ifNotWon((account: PaymentAccount) => this.openPaymentAccountsModal(account, "payout"))}
                      onDeletePayoutAccount={ifNotWon((account: PaymentAccount) => this.openPaymentAccountsDeleteModal(account, "payout"))}
                      onEditDebitAccount={ifNotWon((account: PaymentAccount) => this.openPaymentAccountsModal(account, "debit"))}
                      onDeleteDebitAccount={ifNotWon((account: PaymentAccount) => this.openPaymentAccountsDeleteModal(account, "debit"))}
                    />
                  )}
                  <div
                    style={{
                      marginTop: "40px",
                      marginBottom: "30px",
                    }}
                  >
                    <div
                      style={{
                        border: "1px solid rgb(232 232 232)",
                        padding: "20px",
                        boxSizing: "border-box",
                      }}
                    >
                      <h3 style={{ marginBottom: 16, fontWeight: "bold" }}>
                        Interactions
                      </h3>
                      <Collapse>
                        <Collapse.Panel
                          key="interactions"
                          header={
                            <span>
                              Interactions{" "}
                              <Badge
                                count={this.state.interactions.length}
                                style={tabPaneStyle}
                              />
                            </span>
                          }
                        >
                          <InteractionsSection
                            reference={this.reference}
                            createInteraction={this.createInteraction}
                            interactions={this.state.interactions}
                            showForm={true}
                            showBorder={false}
                            showHeading={true}
                          />
                        </Collapse.Panel>
                        <Collapse.Panel
                          key="pre-opportunity"
                          header={
                            <span>
                              Pre-Opportunity Interactions{" "}
                              <Badge
                                count={
                                  this.state.preOpportunityInteractions.length
                                }
                                style={tabPaneStyle}
                              />
                            </span>
                          }
                        >
                          <PreOpportunityInteractionsSection
                            reference={this.reference}
                            interactions={this.state.preOpportunityInteractions}
                            showForm={false}
                            showBorder={false}
                            showHeading={true}
                          />
                        </Collapse.Panel>
                      </Collapse>
                    </div>
                  </div>
                </div>
              )}
              <Drawer
                title={this.getTitleBasedOnDrawerContent()}
                width={1200}
                onClose={this.toggleCancelModal}
                visible={showDrawer}
                bodyStyle={{ paddingBottom: 80 }}
                maskClosable={false}
              >
                {this.getDrawerContent()}
              </Drawer>
              {this.state.showCancelModal &&
                ModalCreator({
                  title: "Cancel",
                  okType: "primary",
                  okText: "Yes",
                  width: 400,
                  showModal: true,
                  onOkay: this.toggleDrawer(""),
                  toggleModal: this.toggleCancelModal,
                  showFooter: true,
                  inscription: `Are you sure you want to cancel ${spaceCamel(
                    this.state.activeDrawerContent,
                  ).toLocaleLowerCase()} ?`,
                })}
            </Fragment>
          </Card>
        )}
      </Spin>
    );
  }
}
